// components/ProjectDetailsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Stack, Typography, Divider, Button, Paper, CircularProgress, Alert, Pagination,
  Chip, LinearProgress, Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText,
  Drawer,
  TextField,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import ExpandableTextCell from '../utils/ExpandableTextCell';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EastIcon from '@mui/icons-material/East';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useParams } from 'react-router-dom';
import {
  subscribeToConflicts,
  subscribeToFileProcessing,
  subscribeToPromptGeneration
} from '../sse';
import RequirementValidationBlock, { normalizeRequirementValidation } from '../components/RequirementValidationBlock';
import { useAuth } from '../auth/AuthContext';
import {
  buildSourceLabel,
  getRequirementText,
  normalizeRequirement,
  summarizeRequirementMeta,
} from '../utils/requirements';
import {
  isLoadTestingProject,
  normalizeTestingType,
  testingTypeLabel,
} from '../utils/testingType';
import ProjectLlmChatWidget from '../components/ProjectLlmChatWidget';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const EXTRA_PRIMARY_REF = 'primary_requirement_ref';
const EXTRA_RELATED_REF = 'related_requirement_ref';
const EXTRA_PRIMARY_SNAPSHOT = 'primary_requirement_snapshot';
const EXTRA_RELATED_SNAPSHOT = 'related_requirement_snapshot';
const EXTRA_RESOLUTION_SUGGESTION = 'resolution_suggestion';
const CONFLICT_RESERVED_FIELDS = new Set([
  EXTRA_PRIMARY_REF,
  EXTRA_RELATED_REF,
  EXTRA_PRIMARY_SNAPSHOT,
  EXTRA_RELATED_SNAPSHOT,
  EXTRA_RESOLUTION_SUGGESTION,
  'relation',
  'confidence',
  'why',
  'left_validation',
  'right_validation',
  'resolution',
]);

const DEFAULT_CONFLICT_PAGE_SIZE = 100;
const DEFAULT_CONFLICT_RELATIONS = ['CONFLICT', 'DUPLICATE'];

const formatFieldTitle = (key) => {
  if (typeof key !== 'string') return '';
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatRequirementValue = (value, type) => {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  if (type === 'boolean') {
    return value ? 'Да' : 'Нет';
  }
  return String(value);
};

const formatComparisonMode = (mode) => {
  switch (mode) {
    case 'INDEPENDENT':
      return 'Независимо';
    case 'INCLUDE_PREVIOUS':
      return 'С учетом предыдущих';
    default:
      return 'С учетом предыдущих';
  }
};

const REQUIREMENT_FEEDBACK = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

const normalizeRequirementFeedback = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toUpperCase();
  if (normalized === REQUIREMENT_FEEDBACK.APPROVED || normalized === REQUIREMENT_FEEDBACK.REJECTED) {
    return normalized;
  }
  return null;
};

const INSTRUCTIONAL_REWRITE_PATTERNS = [
  /(?:^|[\n\r.!?…]\s*)(уточните|добавьте|замените|разделите|дополните|уберите|завершите|переформулируйте|сформулируйте|опишите|укажите|явно\s+укажите)(?:\s|$|[.,;:!?])/i,
  /(?:^|[\n\r.!?…]\s*)(требуется|необходимо|следует)\s+(уточнить|добавить|заменить|разделить|дополнить|убрать|указать)(?:\s|$|[.,;:!?])/i,
  /(?:^|[\n\r.!?…]\s*)уточните\s+требование(?:\s|$|[.,;:!?])/i,
];

const isInstructionalRewrite = (suggestionText) => {
  if (typeof suggestionText !== 'string') {
    return false;
  }
  const trimmed = suggestionText.trim();
  if (!trimmed) {
    return false;
  }
  return INSTRUCTIONAL_REWRITE_PATTERNS.some((pattern) => pattern.test(trimmed));
};

const sanitizeSuggestedRewrite = (suggestionText) => {
  if (typeof suggestionText !== 'string') {
    return null;
  }

  const normalized = suggestionText.trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return null;
  }

  let cutoff = normalized.length;

  INSTRUCTIONAL_REWRITE_PATTERNS.forEach((pattern) => {
    const match = pattern.exec(normalized);
    if (!match) {
      return;
    }
    if (match.index === 0) {
      cutoff = 0;
      return;
    }
    cutoff = Math.min(cutoff, match.index);
  });

  if (cutoff === 0) {
    return null;
  }

  const cleaned = normalized.slice(0, cutoff).trim();
  if (!cleaned || isInstructionalRewrite(cleaned)) {
    return null;
  }

  return cleaned;
};

const shouldUseSuggestionAsRewrite = (suggestionText) => {
  return Boolean(sanitizeSuggestedRewrite(suggestionText));
};

const FILE_FLOW_STEPS = [
  { key: 'upload', label: 'Загрузка' },
  { key: 'requirements', label: 'Разбор требований' },
  { key: 'conflicts', label: 'Анализ конфликтов' },
  { key: 'done', label: 'Готово' },
];

const MIN_ESTIMATE_SAMPLES = 50;
const PROJECT_PROMPT_MAX_LENGTH = 6000;

const normalizeProcessingStatus = (status) => {
  if (typeof status !== 'string') {
    return null;
  }
  const normalized = status.trim().toUpperCase();
  return normalized.length ? normalized : null;
};

const formatDurationMs = (rawMs) => {
  const ms = Number(rawMs);
  if (!Number.isFinite(ms) || ms <= 0) {
    return null;
  }
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds} сек`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (seconds === 0) {
    return `${minutes} мин`;
  }
  return `${minutes} мин ${seconds} сек`;
};

export default function ProjectDetailsPage() {
  const { id } = useParams(); // projectId
  const navigate = useNavigate();
  const { apiClient, user } = useAuth();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('ADMIN');
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [confluenceTargets, setConfluenceTargets] = useState([]);
  const [filesExpanded, setFilesExpanded] = useState(true);
  const [fileDownloadError, setFileDownloadError] = useState(null);
  const [editedFileDownloadError, setEditedFileDownloadError] = useState(null);
  const [editedFileDownloadSuccess, setEditedFileDownloadSuccess] = useState(null);
  const [downloadingFileIds, setDownloadingFileIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    requirementsPrompt: '',
    conflictsPrompt: '',
  });
  const [projectSaving, setProjectSaving] = useState(false);
  const [projectSaveError, setProjectSaveError] = useState(null);
  const [projectSaveSuccess, setProjectSaveSuccess] = useState(false);
  const [promptDrawerOpen, setPromptDrawerOpen] = useState(false);
  const [promptGenerationJob, setPromptGenerationJob] = useState(null);
  const [promptGenerationError, setPromptGenerationError] = useState(null);
  const [promptDefaultsLoading, setPromptDefaultsLoading] = useState(false);
  const promptGenerationSubscriptionRef = useRef(null);
  const [confOpen, setConfOpen] = useState(false);
  const [confFor, setConfFor] = useState(null);
  const [confItems, setConfItems] = useState([]);
  const [confLoading, setConfLoading] = useState(false);

  // upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [uploadModeDialogOpen, setUploadModeDialogOpen] = useState(false);
  const [uploadSourceDialogOpen, setUploadSourceDialogOpen] = useState(false);
  const [confluenceDialogOpen, setConfluenceDialogOpen] = useState(false);
  const [confluenceUrl, setConfluenceUrl] = useState('');
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [confluenceError, setConfluenceError] = useState(null);
  const [confluenceSuccess, setConfluenceSuccess] = useState(null);
  const [confluenceConnected, setConfluenceConnected] = useState(false);
  const [deletingConfluenceTargetId, setDeletingConfluenceTargetId] = useState(null);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [uploadComparisonMode, setUploadComparisonMode] = useState('INCLUDE_PREVIOUS');
  const fileInputRef = useRef(null);
  const [processingProgress, setProcessingProgress] = useState({});
  const [processingStages, setProcessingStages] = useState({});
  const [lastCompletedFile, setLastCompletedFile] = useState(null);
  const requirementsSectionRef = useRef(null);
  const fileRequirementIdsRef = useRef(new Map());
  const fileProcessingStatusRef = useRef(new Map());

  const processingSubscriptionsRef = useRef(new Map());
  const conflictSubscriptionsRef = useRef(new Map());
  // requirements + pagination
  const [reqPage, setReqPage] = useState({
    content: [],
    total: 0,
    totalPages: 1,
    page: 0,
    size: 20
  });
  const conflictsCacheRef = useRef({});
  const requirementToFileRef = useRef({});
  const confModalStateRef = useRef({ open: false, reqId: null });
  const reqPageRef = useRef(reqPage);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqErr, setReqErr] = useState(null);
  const [projectTab, setProjectTab] = useState('requirements');
  const [llmDebug, setLlmDebug] = useState(null);
  const [llmDebugLoading, setLlmDebugLoading] = useState(false);
  const [llmDebugError, setLlmDebugError] = useState(null);

  const [projectConflicts, setProjectConflicts] = useState([]);
  const [projectConflictsLoading, setProjectConflictsLoading] = useState(true);
  const [projectConflictsError, setProjectConflictsError] = useState(null);
  const conflictsReloadTimerRef = useRef(null);
  const [ignoreDialog, setIgnoreDialog] = useState({ open: false, type: null, conflict: null });
  const [ignoreLoading, setIgnoreLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const isLoadProject = isLoadTestingProject(project);

  const localRequirementsInProgressCount = useMemo(
    () => Object.values(processingStages).filter(stage => stage === 'requirements').length,
    [processingStages]
  );
  const localConflictsInProgressCount = useMemo(
    () => Object.values(processingStages).filter(stage => stage === 'conflicts').length,
    [processingStages]
  );
  const backendStageStats = useMemo(
    () => files.reduce((acc, file) => {
      const status = normalizeProcessingStatus(file?.processingStatus) || (file?.analyzed ? 'DONE' : 'QUEUED');
      if (status === 'QUEUED' || status === 'REQUIREMENTS') {
        acc.requirements += 1;
      } else if (status === 'WAITING_CONFLICTS') {
        acc.waitingConflicts += 1;
      } else if (status === 'CONFLICTS') {
        acc.conflicts += 1;
      }

      if (['QUEUED', 'REQUIREMENTS', 'WAITING_CONFLICTS', 'CONFLICTS'].includes(status)) {
        acc.inFlight += 1;
      }
      return acc;
    }, {
      requirements: 0,
      waitingConflicts: 0,
      conflicts: 0,
      inFlight: 0,
    }),
    [files]
  );
  const waitingConflictsCount = backendStageStats.waitingConflicts;
  const requirementsInProgressCount = Math.max(localRequirementsInProgressCount, backendStageStats.requirements);
  const conflictsInProgressCount = Math.max(
    localConflictsInProgressCount,
    backendStageStats.conflicts + backendStageStats.waitingConflicts
  );
  const processingFilesInProgress = requirementsInProgressCount + conflictsInProgressCount;
  const hasBackendProcessingInFlight = backendStageStats.inFlight > 0;
  const hasFileFlowFeedback = uploading || hasBackendProcessingInFlight || processingFilesInProgress > 0 || Boolean(lastCompletedFile);

  const fileFlowStage = useMemo(() => {
    if (uploading) {
      return 'upload';
    }
    if (requirementsInProgressCount > 0) {
      return 'requirements';
    }
    if (conflictsInProgressCount > 0) {
      return 'conflicts';
    }
    if (lastCompletedFile) {
      return 'done';
    }
    return null;
  }, [conflictsInProgressCount, lastCompletedFile, requirementsInProgressCount, uploading]);

  const fileFlowStageIndex = useMemo(
    () => FILE_FLOW_STEPS.findIndex(step => step.key === fileFlowStage),
    [fileFlowStage]
  );

  const fileFlowProgress = useMemo(() => {
    if (uploading && uploadProgress.total > 0) {
      return Math.round((uploadProgress.done / uploadProgress.total) * 100);
    }
    if (processingFilesInProgress > 0 || hasBackendProcessingInFlight) {
      return null;
    }
    if (lastCompletedFile) {
      return 100;
    }
    return null;
  }, [
    hasBackendProcessingInFlight,
    lastCompletedFile,
    processingFilesInProgress,
    uploadProgress.done,
    uploadProgress.total,
    uploading,
  ]);

  const fileFlowMessage = useMemo(() => {
    if (uploading) {
      return `Загружаем файлы: ${uploadProgress.done} из ${uploadProgress.total}.`;
    }
    if (requirementsInProgressCount > 0) {
      return `Извлекаем требования из файлов: ${requirementsInProgressCount} в обработке.`;
    }
    if (conflictsInProgressCount > 0) {
      if (waitingConflictsCount > 0 && waitingConflictsCount === conflictsInProgressCount) {
        return `Требования извлечены. Ожидаем запуск анализа конфликтов: ${waitingConflictsCount} в очереди.`;
      }
      if (waitingConflictsCount > 0) {
        return `Проверяем конфликты: ${Math.max(0, conflictsInProgressCount - waitingConflictsCount)} в обработке, ${waitingConflictsCount} ожидают запуска.`;
      }
      return `Проверяем конфликты: ${conflictsInProgressCount} в обработке.`;
    }
    if (lastCompletedFile) {
      return `Обработка файла «${lastCompletedFile.fileName || 'Файл'}» завершена.`;
    }
    if (hasBackendProcessingInFlight) {
      return 'Обновляем статусы обработки файлов.';
    }
    return '';
  }, [
    conflictsInProgressCount,
    hasBackendProcessingInFlight,
    lastCompletedFile,
    requirementsInProgressCount,
    uploadProgress.done,
    uploadProgress.total,
    waitingConflictsCount,
    uploading,
  ]);

  const isPromptGenerationBusy = Boolean(promptGenerationJob?.active);
  const llmInteractionLocked = isPromptGenerationBusy;
  const isTestGenerationBusy = false;

  const stopPromptGenerationSubscription = useCallback(() => {
    if (!promptGenerationSubscriptionRef.current) {
      return;
    }
    try {
      promptGenerationSubscriptionRef.current();
    } catch (e) {
      // ignore
    }
    promptGenerationSubscriptionRef.current = null;
  }, []);

  const fetchAllPages = useCallback(async (path, { params = {}, pageSize = DEFAULT_CONFLICT_PAGE_SIZE, mapper } = {}) => {
    const numericSize = Number(pageSize);
    const size = Number.isFinite(numericSize) && numericSize > 0 ? Math.trunc(numericSize) : DEFAULT_CONFLICT_PAGE_SIZE;
    const baseParams = { ...params };
    const requestedPage = Number(baseParams.page);
    let page = Number.isFinite(requestedPage) && requestedPage >= 0 ? Math.trunc(requestedPage) : 0;
    delete baseParams.page;

    const normalizeParamValue = (value) => {
      if (Array.isArray(value)) {
        const filtered = value
          .map(item => (typeof item === 'string' ? item.trim() : item))
          .filter(item => item != null && !(typeof item === 'string' && item.length === 0));
        if (filtered.length === 0) {
          return undefined;
        }
        return filtered.join(',');
      }
      return value;
    };

    const aggregated = [];
    let totalElements = null;
    let totalPages = null;

    for (let safety = 0; safety < 1000; safety += 1) {
      const requestParams = Object.entries({ ...baseParams, page, size })
        .reduce((acc, [key, value]) => {
          const normalized = normalizeParamValue(value);
          if (normalized !== undefined) {
            acc[key] = normalized;
          }
          return acc;
        }, {});

      const response = await apiClient.get(path, { params: requestParams });
      const data = response?.data ?? {};

      const rawContent = Array.isArray(data?.content)
        ? data.content
        : Array.isArray(data)
          ? data
          : [];

      let mappedContent = rawContent;
      if (mapper) {
        mappedContent = rawContent
          .map(item => mapper(item))
          .filter(item => item != null);
      }

      aggregated.push(...mappedContent);

      if (data.empty === true || data.last === true || rawContent.length === 0) {
        break;
      }

      if (totalElements == null && typeof data.totalElements === 'number') {
        totalElements = data.totalElements;
      }
      if (totalPages == null && typeof data.totalPages === 'number') {
        totalPages = data.totalPages;
      }

      if ((totalElements != null && aggregated.length >= totalElements)
        || (totalPages != null && page + 1 >= totalPages)
        || rawContent.length < size) {
        break;
      }

      page += 1;
    }

    return aggregated;
  }, [apiClient]);

  // кеш количества конфликтов на текущих req (id -> count)
  const [conflictMap, setConflictMap] = useState({}); // { [stableId]: number }
  const [expandedReqTexts, setExpandedReqTexts] = useState(() => new Set());
  const [editDialog, setEditDialog] = useState({
    open: false,
    reqId: null,
    text: '',
    enabled: true,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [applyRewriteDialog, setApplyRewriteDialog] = useState({
    open: false,
    reqId: null,
    seqNumber: null,
    suggestedText: '',
  });
  const [applyRewriteLoadingMap, setApplyRewriteLoadingMap] = useState({});
  const [autoRewriteLoadingMap, setAutoRewriteLoadingMap] = useState({});
  const [feedbackSavingMap, setFeedbackSavingMap] = useState({});
  const [applyRewriteError, setApplyRewriteError] = useState(null);
  const autoRewritePrefetchAttemptedRef = useRef(new Set());
  const autoRewritePrefetchInFlightRef = useRef(new Set());

  const pagesCount = reqPage.totalPages ?? Math.max(1, Math.ceil((reqPage.total || 0) / (reqPage.size || 1)));

  const sortedRequirements = useMemo(() => (
    Array.isArray(reqPage?.content) ? reqPage.content : []
  ), [reqPage?.content]);

  useEffect(() => {
    autoRewritePrefetchAttemptedRef.current = new Set();
    autoRewritePrefetchInFlightRef.current = new Set();
  }, [id]);

  const patchFileRuntimeState = useCallback((fileId, patch) => {
    if (!fileId || !patch || typeof patch !== 'object') {
      return;
    }
    setFiles((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return prev;
      }
      let changed = false;
      const next = prev.map((item) => {
        if (!item || item.id !== fileId) {
          return item;
        }
        changed = true;
        return { ...item, ...patch };
      });
      return changed ? next : prev;
    });
  }, []);

  const loadProjectFiles = useCallback(({ silent = false } = {}) => {
    return Promise.all([
      apiClient.get(`/api/projects/${id}/files`),
      apiClient.get('/api/ingest/confluence/targets', { params: { projectId: id } })
    ])
      .then(([filesResponse, targetsResponse]) => {
        setFiles(filesResponse?.data || []);
        setConfluenceTargets(targetsResponse?.data || []);
      })
      .catch((error) => {
        if (!silent) {
          setErr(error?.response?.data?.error || error.message);
        }
      });
  }, [apiClient, id]);

  const loadProject = useCallback(() => {
    setErr(null);
    setFileDownloadError(null);
    setLoading(true);
    Promise.all([
      apiClient.get(`/api/projects/${id}`),
      apiClient.get(`/api/projects/${id}/files`),
      apiClient.get('/api/ingest/confluence/targets', { params: { projectId: id } })
    ])
      .then(([p, f, targets]) => {
        setProject(p.data);
        setFiles(f.data || []);
        setConfluenceTargets(targets.data || []);
        setProjectSaveSuccess(false);
        setProjectSaveError(null);
      })
      .catch(e => setErr(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [apiClient, id]);

  const loadReq = useCallback((page, size) => {
    setReqErr(null);
    setReqLoading(true);
    apiClient.get(`/api/projects/${id}/requirements`, { params: { page, size } })
      .then(res => {
        const d = res.data || {};
        const totalElements = typeof d.totalElements === 'number' ? d.totalElements : (typeof d.total === 'number' ? d.total : 0);
        const totalPages = typeof d.totalPages === 'number'
          ? d.totalPages
          : Math.max(1, Math.ceil(totalElements / (d.size || size || 1)));
        const pageNumber = typeof d.number === 'number' ? d.number : (typeof d.page === 'number' ? d.page : page);
        const pageSize = typeof d.size === 'number' ? d.size : size;
        const contentRaw = Array.isArray(d.content) ? d.content : [];
        const content = contentRaw.map(normalizeRequirement);

        setProject((prev) => {
          if (!prev || prev.requirementsCount === totalElements) {
            return prev;
          }
          return { ...prev, requirementsCount: totalElements };
        });

        setReqPage({
          content,
          total: totalElements,
          totalPages,
          page: pageNumber,
          size: pageSize
        });

        content.forEach(item => {
          const rid = item?.stableId || item?.id;
          const sourceFileId = item?.sourceFileId || item?.fileId;
          if (rid && sourceFileId) {
            requirementToFileRef.current[rid] = sourceFileId;
            const existing = fileRequirementIdsRef.current.get(sourceFileId) || new Set();
            existing.add(`id:${rid}`);
            fileRequirementIdsRef.current.set(sourceFileId, existing);
          }
        });

        setLastCompletedFile((prev) => {
          if (!prev?.fileId) {
            return prev;
          }
          const count = fileRequirementIdsRef.current.get(prev.fileId)?.size || 0;
          if (prev.requirementsCount === count) {
            return prev;
          }
          return { ...prev, requirementsCount: count };
        });
      })
      .catch(e => setReqErr(e?.response?.data?.error || e.message))
      .finally(() => setReqLoading(false));
  }, [apiClient, id]);

  const reloadRequirements = useCallback(() => {
    const current = reqPageRef.current || {};
    const page = typeof current.page === 'number' ? current.page : 0;
    const size = typeof current.size === 'number' ? current.size : 20;
    loadReq(page, size);
  }, [loadReq]);

  const loadLlmDebug = useCallback(() => {
    if (!isAdmin) {
      setLlmDebug(null);
      setLlmDebugError(null);
      setLlmDebugLoading(false);
      return;
    }
    setLlmDebugError(null);
    setLlmDebugLoading(true);
    apiClient.get(`/api/projects/${id}/llm-debug`)
      .then((res) => {
        setLlmDebug(res?.data || null);
      })
      .catch((e) => {
        setLlmDebugError(e?.response?.data?.error || e.message);
      })
      .finally(() => setLlmDebugLoading(false));
  }, [apiClient, id, isAdmin]);

  useEffect(() => {
    fileRequirementIdsRef.current = new Map();
    fileProcessingStatusRef.current = new Map();
    setLastCompletedFile(null);
    setPromptGenerationJob(null);
    setPromptGenerationError(null);
  }, [id]);

  useEffect(() => {
    loadProject();
    loadReq(0, 20);
  }, [apiClient, id, loadProject, loadReq]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (confluenceTargets.some((target) => target?.processingStatus === 'QUEUED' || target?.processingStatus === 'PROCESSING')) {
        loadProjectFiles({ silent: true });
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [confluenceTargets, loadProjectFiles]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }
    if (!(processingFilesInProgress > 0 || hasBackendProcessingInFlight)) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadProjectFiles({ silent: true });
    }, 3000);
    return () => clearInterval(timer);
  }, [hasBackendProcessingInFlight, loadProjectFiles, processingFilesInProgress]);

  useEffect(() => {
    if (!isAdmin) {
      setProjectTab('requirements');
      setLlmDebug(null);
      setLlmDebugError(null);
      setLlmDebugLoading(false);
      return undefined;
    }

    loadLlmDebug();
    const timer = setInterval(() => {
      loadLlmDebug();
    }, 15000);
    return () => clearInterval(timer);
  }, [isAdmin, loadLlmDebug]);

  useEffect(() => {
    if (project) {
      setProjectForm({
        name: project.name || '',
        requirementsPrompt: project.requirementsPrompt || '',
        conflictsPrompt: project.conflictsPrompt || '',
      });
    }
  }, [project]);

  const handleProjectFieldChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setProjectForm(prev => ({ ...prev, [field]: value }));
    setProjectSaveError(null);
    setProjectSaveSuccess(false);
  };

  const handleGoToStepTwo = useCallback(() => {
    const nextStepPath = isLoadProject
      ? `/projects/${id}/test-cases`
      : `/projects/${id}/story-qa`;
    navigate(nextStepPath);
  }, [id, isLoadProject, navigate]);

  const handlePromptFieldChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    const normalizedValue = value.length > PROJECT_PROMPT_MAX_LENGTH
      ? value.slice(0, PROJECT_PROMPT_MAX_LENGTH)
      : value;
    setProjectForm(prev => ({ ...prev, [field]: normalizedValue }));
    setProjectSaveError(null);
    setProjectSaveSuccess(false);
  };

  const startPromptGenerationSubscription = useCallback((jobId) => {
    if (!jobId) {
      return;
    }

    stopPromptGenerationSubscription();

    const stop = subscribeToPromptGeneration(id, jobId, {
      onProgress: (payload) => {
        const progress = Number(payload?.progress);
        const status = typeof payload?.status === 'string' ? payload.status : undefined;
        const message = typeof payload?.message === 'string' ? payload.message : undefined;
        setPromptGenerationJob(prev => {
          if (!prev || prev.jobId !== jobId) {
            return prev;
          }
          return {
            ...prev,
            status: status || prev.status,
            progress: Number.isFinite(progress) ? Math.max(0, Math.min(100, Math.round(progress))) : prev.progress,
            message: message || prev.message,
            active: !['COMPLETE', 'ERROR'].includes((status || prev.status || '').toUpperCase()),
          };
        });
      },
      onReset: () => {
        setProjectForm(prev => ({
          ...prev,
          requirementsPrompt: '',
          conflictsPrompt: '',
        }));
        setProjectSaveSuccess(false);
      },
      onRequirementsChunk: (payload) => {
        const chunk = typeof payload === 'string' ? payload : payload?.chunk;
        if (!chunk) {
          return;
        }
        setProjectForm(prev => ({
          ...prev,
          requirementsPrompt: `${prev.requirementsPrompt || ''}${chunk}`.slice(0, PROJECT_PROMPT_MAX_LENGTH),
        }));
        setProjectSaveSuccess(false);
      },
      onConflictsChunk: (payload) => {
        const chunk = typeof payload === 'string' ? payload : payload?.chunk;
        if (!chunk) {
          return;
        }
        setProjectForm(prev => ({
          ...prev,
          conflictsPrompt: `${prev.conflictsPrompt || ''}${chunk}`.slice(0, PROJECT_PROMPT_MAX_LENGTH),
        }));
        setProjectSaveSuccess(false);
      },
      onComplete: (payload) => {
        promptGenerationSubscriptionRef.current = null;
        const requirementsPrompt = typeof payload?.requirementsPrompt === 'string'
          ? payload.requirementsPrompt
          : null;
        const conflictsPrompt = typeof payload?.conflictsPrompt === 'string'
          ? payload.conflictsPrompt
          : null;
        if (requirementsPrompt != null || conflictsPrompt != null) {
          setProjectForm(prev => ({
            ...prev,
            requirementsPrompt: requirementsPrompt != null
              ? requirementsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH)
              : prev.requirementsPrompt,
            conflictsPrompt: conflictsPrompt != null
              ? conflictsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH)
              : prev.conflictsPrompt,
          }));
        }
        setPromptGenerationJob(prev => {
          if (!prev || prev.jobId !== jobId) {
            return prev;
          }
          return {
            ...prev,
            status: 'COMPLETE',
            progress: 100,
            message: typeof payload?.message === 'string' ? payload.message : 'Генерация промтов завершена.',
            completed: true,
            error: false,
            active: false,
          };
        });
        setPromptGenerationError(null);
      },
      onError: (payload) => {
        promptGenerationSubscriptionRef.current = null;
        const message = typeof payload === 'string'
          ? payload
          : (payload?.message || 'Не удалось сгенерировать промты.');
        setPromptGenerationError(String(message));
        setPromptGenerationJob(prev => {
          if (!prev || prev.jobId !== jobId) {
            return prev;
          }
          return {
            ...prev,
            status: 'ERROR',
            message: String(message),
            completed: true,
            error: true,
            active: false,
          };
        });
      },
    });

    promptGenerationSubscriptionRef.current = stop;
  }, [id, stopPromptGenerationSubscription]);

  const handleGeneratePromptsWithLlm = useCallback(async () => {
    if (isPromptGenerationBusy) {
      return;
    }

    setPromptGenerationError(null);
    setProjectSaveError(null);
    setProjectSaveSuccess(false);

    try {
      const response = await apiClient.post(`/api/projects/${id}/prompts/generate`);
      const job = response?.data;
      const jobId = job?.jobId;
      if (!jobId) {
        throw new Error('Сервер не вернул id генерации промтов.');
      }

      const nextRequirementsPrompt = typeof job?.requirementsPrompt === 'string' ? job.requirementsPrompt : '';
      const nextConflictsPrompt = typeof job?.conflictsPrompt === 'string' ? job.conflictsPrompt : '';
      setProjectForm(prev => ({
        ...prev,
        requirementsPrompt: nextRequirementsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH),
        conflictsPrompt: nextConflictsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH),
      }));

      const normalizedStatus = typeof job?.status === 'string' ? job.status.toUpperCase() : 'PENDING';
      const active = !(job?.completed || job?.error || normalizedStatus === 'COMPLETE' || normalizedStatus === 'ERROR');
      setPromptGenerationJob({
        ...job,
        status: normalizedStatus,
        progress: Number.isFinite(Number(job?.progress)) ? Math.max(0, Math.min(100, Math.round(Number(job.progress)))) : 0,
        message: typeof job?.message === 'string' ? job.message : 'Подготовка генерации промтов…',
        completed: Boolean(job?.completed),
        error: Boolean(job?.error),
        active,
      });

      if (active) {
        startPromptGenerationSubscription(jobId);
      } else {
        stopPromptGenerationSubscription();
      }
    } catch (e) {
      setPromptGenerationError(e?.response?.data?.error || e.message || 'Не удалось запустить генерацию промтов.');
    }
  }, [
    apiClient,
    id,
    isPromptGenerationBusy,
    startPromptGenerationSubscription,
    stopPromptGenerationSubscription,
  ]);

  const handleResetPromptsToDefault = useCallback(async () => {
    if (isPromptGenerationBusy || promptDefaultsLoading) {
      return;
    }

    setPromptDefaultsLoading(true);
    setPromptGenerationError(null);
    setProjectSaveError(null);
    setProjectSaveSuccess(false);
    try {
      const response = await apiClient.get(`/api/projects/${id}/prompts/defaults`);
      const defaults = response?.data || {};
      setProjectForm(prev => ({
        ...prev,
        requirementsPrompt: (defaults.requirementsPrompt || '').slice(0, PROJECT_PROMPT_MAX_LENGTH),
        conflictsPrompt: (defaults.conflictsPrompt || '').slice(0, PROJECT_PROMPT_MAX_LENGTH),
      }));
    } catch (e) {
      setProjectSaveError(e?.response?.data?.error || e.message || 'Не удалось получить дефолтные промты.');
    } finally {
      setPromptDefaultsLoading(false);
    }
  }, [apiClient, id, isPromptGenerationBusy, promptDefaultsLoading]);

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/api/projects/${id}/prompts/jobs/active`)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const job = response?.data;
        if (!job?.jobId) {
          return;
        }
        const normalizedStatus = typeof job?.status === 'string' ? job.status.toUpperCase() : 'PENDING';
        const active = !(job?.completed || job?.error || normalizedStatus === 'COMPLETE' || normalizedStatus === 'ERROR');
        setPromptGenerationJob({
          ...job,
          status: normalizedStatus,
          progress: Number.isFinite(Number(job?.progress)) ? Math.max(0, Math.min(100, Math.round(Number(job.progress)))) : 0,
          message: typeof job?.message === 'string' ? job.message : 'Подготовка генерации промтов…',
          completed: Boolean(job?.completed),
          error: Boolean(job?.error),
          active,
        });
        if (typeof job?.requirementsPrompt === 'string' || typeof job?.conflictsPrompt === 'string') {
          setProjectForm(prev => ({
            ...prev,
            requirementsPrompt: typeof job?.requirementsPrompt === 'string'
              ? job.requirementsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH)
              : prev.requirementsPrompt,
            conflictsPrompt: typeof job?.conflictsPrompt === 'string'
              ? job.conflictsPrompt.slice(0, PROJECT_PROMPT_MAX_LENGTH)
              : prev.conflictsPrompt,
          }));
        }
        if (active) {
          startPromptGenerationSubscription(job.jobId);
        }
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          return;
        }
        if (!cancelled) {
          setPromptGenerationError(error?.response?.data?.error || error.message);
        }
      });

    return () => {
      cancelled = true;
      stopPromptGenerationSubscription();
    };
  }, [apiClient, id, startPromptGenerationSubscription, stopPromptGenerationSubscription]);

  const renderSnapshot = useCallback((title, snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }
    const entries = Object.entries(snapshot)
      .filter(([key, value]) => key && value != null && value !== '');
    if (entries.length === 0) {
      return null;
    }
    return (
      <Stack sx={{ mt: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {entries.map(([key, value]) => (
          <Typography key={key} variant="body2">
            <b>{key}:</b> {formatRequirementValue(value)}
          </Typography>
        ))}
      </Stack>
    );
  }, []);

  const normalizeConflict = useCallback((raw) => {
    if (!raw || typeof raw !== 'object') {
      return raw;
    }

    let extra = {};
    if (raw.extra && typeof raw.extra === 'object' && !Array.isArray(raw.extra)) {
      extra = raw.extra;
    } else if (typeof raw.extra === 'string') {
      try {
        const parsed = JSON.parse(raw.extra);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          extra = parsed;
        }
      } catch (e) {
        /* noop */
      }
    }

    const explanation = [raw.rationale, raw.explanation, raw.why]
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .find((value) => Boolean(value)) || null;

    let resolutionSuggestion = null;
    const resolutionCandidate = typeof raw.resolution === 'string' ? raw.resolution.trim() : null;
    if (typeof raw.resolutionSuggestion === 'string' && raw.resolutionSuggestion.trim()) {
      resolutionSuggestion = raw.resolutionSuggestion.trim();
    } else if (resolutionCandidate) {
      resolutionSuggestion = resolutionCandidate;
    } else if (extra && Object.prototype.hasOwnProperty.call(extra, EXTRA_RESOLUTION_SUGGESTION)) {
      const fromExtra = extra[EXTRA_RESOLUTION_SUGGESTION];
      if (typeof fromExtra === 'string' && fromExtra.trim()) {
        resolutionSuggestion = fromExtra.trim();
      } else if (fromExtra != null) {
        const text = String(fromExtra).trim();
        if (text) {
          resolutionSuggestion = text;
        }
      }
    }

    let idValue = raw.id;
    if (idValue == null && raw.conflictId != null) {
      idValue = raw.conflictId;
    }
    if (idValue == null && raw.linkId != null) {
      idValue = raw.linkId;
    }
    const id = idValue == null ? null : String(idValue);

    const primaryRequirementId = typeof raw.requirementStableId === 'string'
      ? raw.requirementStableId
      : (typeof raw.requirementId === 'string' ? raw.requirementId : null);

    return {
      ...raw,
      id,
      extra,
      resolutionSuggestion,
      explanation,
      rationale: explanation || (typeof raw.rationale === 'string' ? raw.rationale.trim() : raw.rationale),
      primaryRequirementId: primaryRequirementId || raw.primaryRequirementId || raw.primaryRequirementStableId,
    };
  }, []);

  const resolveRequirementId = useCallback((conflict) => {
    if (!conflict || typeof conflict !== 'object') {
      return null;
    }

    const pick = (value) => {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return null;
    };

    const pickFromRef = (ref) => {
      if (!ref || typeof ref !== 'object') {
        return null;
      }
      return pick(ref.stableId)
        || pick(ref.id)
        || pick(ref.requirementStableId)
        || pick(ref.requirementId);
    };

    const requirementArray = Array.isArray(conflict.requirements) ? conflict.requirements : [];
    let fromArray = null;
    for (let idx = 0; idx < requirementArray.length; idx += 1) {
      const candidate = pick(requirementArray[idx]) || pickFromRef(requirementArray[idx]);
      if (candidate) {
        fromArray = candidate;
        break;
      }
    }

    return pick(conflict.requirementStableId)
      || pick(conflict.requirementId)
      || pick(conflict.targetRequirementId)
      || pick(conflict.stableId)
      || pick(conflict.primaryRequirementId)
      || pick(conflict.primaryRequirementStableId)
      || pick(conflict.leftRequirementId)
      || pick(conflict.leftRequirementStableId)
      || pick(conflict.primaryId)
      || pick(conflict.primaryStableId)
      || pickFromRef(conflict.requirement)
      || pickFromRef(conflict.primary)
      || pickFromRef(conflict.left)
      || pickFromRef(conflict.right)
      || (typeof conflict.extra === 'object' && conflict.extra && pick(conflict.extra[EXTRA_PRIMARY_REF]))
      || fromArray;
  }, []);

  const fetchRequirementConflicts = useCallback(async (reqStableId, options = {}) => {
    if (!reqStableId) {
      return [];
    }

    const { pageSize = DEFAULT_CONFLICT_PAGE_SIZE, params = {} } = options;
    const mergedParams = { relation: DEFAULT_CONFLICT_RELATIONS, ...params };

    return fetchAllPages(`/api/projects/${id}/requirements/${reqStableId}/conflicts`, {
      params: mergedParams,
      pageSize,
      mapper: normalizeConflict,
    });
  }, [fetchAllPages, id, normalizeConflict]);

  const handleSaveProject = () => {
    if (isPromptGenerationBusy) {
      setProjectSaveError('Дождитесь завершения генерации промтов перед сохранением.');
      return;
    }
    const name = projectForm.name.trim();
    if (!name) {
      setProjectSaveError('Название проекта не может быть пустым.');
      return;
    }

    const projectType = project?.projectType || 'REQUIREMENTS_ONLY';
    const webProjectSubtype = project?.webProjectSubtype || null;
    const testingType = normalizeTestingType(project?.testingType);
    const requirementsPrompt = projectForm.requirementsPrompt ?? '';
    const conflictsPrompt = projectForm.conflictsPrompt ?? '';
    if (requirementsPrompt.length > PROJECT_PROMPT_MAX_LENGTH) {
      setProjectSaveError(`Промт извлечения требований не может превышать ${PROJECT_PROMPT_MAX_LENGTH} символов.`);
      return;
    }
    if (conflictsPrompt.length > PROJECT_PROMPT_MAX_LENGTH) {
      setProjectSaveError(`Промт анализа конфликтов не может превышать ${PROJECT_PROMPT_MAX_LENGTH} символов.`);
      return;
    }

    setProjectSaving(true);
    setProjectSaveError(null);

    apiClient.put(`/api/projects/${id}`, {
      name,
      projectType,
      webProjectSubtype,
      testingType,
      requirementsPrompt,
      conflictsPrompt,
    })
      .then(res => {
        const data = res?.data || null;
        if (data) {
          setProject(data);
          setProjectForm({
            name: data.name || '',
            requirementsPrompt: data.requirementsPrompt || '',
            conflictsPrompt: data.conflictsPrompt || '',
          });
        }
        setProjectSaveSuccess(true);
      })
      .catch(e => {
        setProjectSaveError(e?.response?.data?.error || e.message);
      })
      .finally(() => {
        setProjectSaving(false);
      });
  };

  useEffect(() => {
    setExpandedReqTexts(new Set());
  }, [reqPage.page, reqPage.content]);

  useEffect(() => {
    reqPageRef.current = reqPage;
  }, [reqPage]);

  useEffect(() => {
    confModalStateRef.current = { open: confOpen, reqId: confFor };
  }, [confOpen, confFor]);

  const requirementsPromptLength = (projectForm.requirementsPrompt || '').length;
  const conflictsPromptLength = (projectForm.conflictsPrompt || '').length;
  const isRequirementsPromptTooLong = requirementsPromptLength > PROJECT_PROMPT_MAX_LENGTH;
  const isConflictsPromptTooLong = conflictsPromptLength > PROJECT_PROMPT_MAX_LENGTH;
  const hasPromptLengthOverflow = isRequirementsPromptTooLong || isConflictsPromptTooLong;

  useEffect(() => {
    return () => {
      stopPromptGenerationSubscription();

      processingSubscriptionsRef.current.forEach(unsub => {
        try { unsub(); } catch (e) { /* noop */ }
      });
      processingSubscriptionsRef.current.clear();

      conflictSubscriptionsRef.current.forEach(unsub => {
        try { unsub(); } catch (e) { /* noop */ }
      });
      conflictSubscriptionsRef.current.clear();

      setProcessingStages({});

      if (conflictsReloadTimerRef.current) {
        clearTimeout(conflictsReloadTimerRef.current);
        conflictsReloadTimerRef.current = null;
      }
    };
  }, [stopPromptGenerationSubscription]);

  // префетчим количество конфликтов для видимых требований страницы (лениво + кеш)
  useEffect(() => {
    const ids = (reqPage.content || [])
      .map(r => r.stableId || r.id)
      .filter(Boolean);

    const missing = ids.filter(x => conflictMap[x] === undefined);
    if (missing.length === 0) return;

    Promise.all(
      missing.map(reqId =>
        fetchRequirementConflicts(reqId)
          .then(items => [reqId, items])
          .catch(() => [reqId, null])
      )
    ).then(pairs => {
      pairs.forEach(([rid, items]) => {
        if (Array.isArray(items)) {
          conflictsCacheRef.current[rid] = items;
        }
      });
      setConflictMap(prev => {
        const next = { ...prev };
        pairs.forEach(([rid, items]) => {
          if (Array.isArray(items)) {
            next[rid] = items.length;
          }
        });
        return next;
      });
    });
  }, [fetchRequirementConflicts, reqPage.content, conflictMap]); // пересчитываем при смене проекта/страницы

  const openConflicts = (reqStableId) => {
    if (!reqStableId) return;

    setConfFor(reqStableId);
    setConfItems([]);
    setConfOpen(true);
    setConfLoading(true);

    const cached = conflictsCacheRef.current[reqStableId];
    if (Array.isArray(cached)) {
      setConfItems(cached);
      setConfLoading(false);
    } else {
      fetchRequirementConflicts(reqStableId)
        .then(items => {
          conflictsCacheRef.current[reqStableId] = items;
          setConfItems(items);
        })
        .catch(() => setConfItems([]))
        .finally(() => setConfLoading(false));
    }

    const fileId = requirementToFileRef.current[reqStableId];
    if (fileId) {
      startConflictsSubscription(fileId);
    }
  };

  const handleCloseConflicts = () => {
    if (confFor) {
      const fileId = requirementToFileRef.current[confFor];
      if (fileId) {
        const unsubscribe = conflictSubscriptionsRef.current.get(fileId);
        if (unsubscribe) {
          try { unsubscribe(); } catch (e) { /* noop */ }
          conflictSubscriptionsRef.current.delete(fileId);
        }
        setProcessingStages(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
      }
    }
    setConfOpen(false);
    setConfLoading(false);
    setConfItems([]);
    setConfFor(null);
  };

  const TYPE_META = {
    FUNCTIONAL: { label: 'Функциональное', color: 'primary' },
    NON_FUNCTIONAL: { label: 'Нефункциональное', color: 'success' },
    CONSTRAINT: { label: 'Ограничение', color: 'warning' },
    DOUBTFUL: { label: 'Сомнительное', color: 'secondary' },
  };

  const TypeChip = ({ type }) => {
    const t = (type || '').toUpperCase();
    const { label, color } = TYPE_META[t] || { label: t || '-', color: 'default' };
    return <Chip size="small" label={label} color={color} variant="outlined" />;
  };

  const getRelationColor = (relation) => {
    const rel = (relation || '').toUpperCase();
    if (rel === 'DUPLICATE') return 'info';
    if (rel === 'SUPERSET') return 'warning';
    return 'error';
  };

  const formatRelationLabel = (relation, score) => {
    const rel = relation || 'CONFLICT';
    if (score == null) {
      return rel;
    }

    const numeric = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(numeric)) {
      return rel;
    }

    return `${rel} · ${numeric.toFixed(2)}`;
  };

  const RequirementSummary = ({ title, requirement, compact }) => {
    const validation = normalizeRequirementValidation(requirement);
    const fullText = getRequirementText(requirement);
    const metaEntries = summarizeRequirementMeta(requirement);
    const sourceLabel = buildSourceLabel(requirement);

    return (
      <Stack
        spacing={1.5}
        sx={{
          flex: 1,
          minWidth: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
        }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {!requirement ? (
          <Typography variant="body2" color="text.secondary">Данные недоступны.</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {requirement.type && <TypeChip type={requirement.type} />}
              {requirement.stableId && (
                <Typography variant="caption" color="text.secondary">
                  ID: {requirement.stableId}
                </Typography>
              )}
            </Stack>
            {fullText && (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {fullText}
              </Typography>
            )}
            <Stack spacing={0.5}>
              {metaEntries.map(({ label, value }) => (
                <Typography key={label} variant="body2">
                  <b>{label}:</b> {value}
                </Typography>
              ))}
              {sourceLabel && !metaEntries.some((m) => m.label === 'Источник') && (
                <Typography variant="body2">
                  <b>Источник:</b> {sourceLabel}
                </Typography>
              )}
            </Stack>
            {validation && (
              <RequirementValidationBlock validation={validation} compact={compact} />
            )}
          </>
        )}
      </Stack>
    );
  };

  const toggleFullText = useCallback((id) => {
    if (!id) return;
    setExpandedReqTexts(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const formatDebugNumber = (value, digits = 2) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) {
      return '—';
    }
    return numeric.toFixed(digits);
  };

  const renderPlaceholder = () => (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  );

  const conflictIssuePatterns = useMemo(() => [
    /конфликт/i,
    /требование\s*№/i,
  ], []);

  const isConflictIssue = useCallback((text) => {
    if (typeof text !== 'string') {
      return false;
    }
    return conflictIssuePatterns.some((pattern) => pattern.test(text));
  }, [conflictIssuePatterns]);

  const renderIssuesCell = (issues) => {
    if (!Array.isArray(issues) || issues.length === 0) {
      return renderPlaceholder();
    }

    const normalized = issues
      .map((issue, idx) => {
        const display = typeof issue === 'string' ? issue : String(issue);
        return { idx, display, trimmed: display.trim() };
      })
      .filter(({ trimmed }) => trimmed.length > 0)
      .filter(({ trimmed }) => !isConflictIssue(trimmed));

    if (normalized.length === 0) {
      return renderPlaceholder();
    }

    return (
      <Stack spacing={0.5}>
        {normalized.map(({ idx, display }) => (
          <Typography
            key={idx}
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            • {display}
          </Typography>
        ))}
      </Stack>
    );
  };

  const renderSuggestedRewriteCell = (text, req) => {
    if (typeof text !== 'string') {
      return renderPlaceholder();
    }

    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return renderPlaceholder();
    }

    const rid = req?.stableId || req?.id;
    const isApplying = rid ? Boolean(applyRewriteLoadingMap[rid]) : false;

    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text}
        </Typography>

        {rid && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openApplyRewriteDialog(req, text)}
            disabled={isApplying || reqLoading}
          >
            {isApplying ? <CircularProgress size={16} /> : 'Применить'}
          </Button>
        )}
      </Stack>
    );
  };

  const requirementTableColumns = 7; // № + 6 колонок с данными

  const handleIncomingRequirements = useCallback((fileId, incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) {
      return;
    }

    const normalizedIncoming = incoming.map(normalizeRequirement);
    const knownRequirementKeys = fileRequirementIdsRef.current.get(fileId) || new Set();

    normalizedIncoming.forEach((item, index) => {
      const rid = item?.stableId || item?.id;
      if (rid) {
        knownRequirementKeys.add(`id:${rid}`);
        return;
      }

      const seq = item?.seqNumber ?? item?.chunkIndex ?? index;
      const text = getRequirementText(item);
      const fallbackKey = text && text.trim()
        ? `draft:${seq}:${text.trim().toLowerCase()}`
        : `draft:${seq}:${index}`;
      knownRequirementKeys.add(fallbackKey);
    });

    fileRequirementIdsRef.current.set(fileId, knownRequirementKeys);
    setLastCompletedFile(prev => {
      if (!prev || prev.fileId !== fileId) {
        return prev;
      }
      if (prev.requirementsCount === knownRequirementKeys.size) {
        return prev;
      }
      return { ...prev, requirementsCount: knownRequirementKeys.size };
    });

    normalizedIncoming.forEach(item => {
      const rid = item?.stableId || item?.id;
      if (rid) {
        requirementToFileRef.current[rid] = fileId;
      }
    });

    let uniqueAdded = 0;
    setReqPage(prev => {
      const prevList = Array.isArray(prev.content) ? prev.content : [];
      const prevIds = new Set(prevList.map(item => (item?.stableId || item?.id)).filter(Boolean));
      let newUniqueCount = 0;
      normalizedIncoming.forEach(item => {
        const rid = item?.stableId || item?.id;
        if (rid) {
          if (!prevIds.has(rid)) {
            newUniqueCount += 1;
          }
        } else {
          newUniqueCount += 1;
        }
      });

      const nextContent = [...normalizedIncoming, ...prevList];
      const seen = new Set();
      const deduped = [];
      nextContent.forEach(item => {
        const key = item?.stableId || item?.id;
        if (key) {
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(item);
          }
        } else {
          deduped.push(item);
        }
      });
      const nextTotal = (prev.total || 0) + newUniqueCount;
      const pageSize = prev.size || 1;

      uniqueAdded = newUniqueCount;
      return {
        ...prev,
        content: deduped,
        total: nextTotal,
        totalPages: Math.max(1, Math.ceil(nextTotal / pageSize)),
      };
    });

    if (uniqueAdded > 0) {
      setProject(prev => prev ? ({
        ...prev,
        requirementsCount: (prev.requirementsCount || 0) + uniqueAdded,
      }) : prev);
    }

    setConflictMap(prev => {
      const next = { ...prev };
      normalizedIncoming.forEach(item => {
        const rid = item?.stableId || item?.id;
        if (rid && next[rid] == null) {
          next[rid] = 0;
        }
      });
      return next;
    });
  }, []);

  const loadProjectConflicts = useCallback(() => {
    setProjectConflictsError(null);
    setProjectConflictsLoading(true);
    fetchAllPages(`/api/projects/${id}/conflicts`, {
      params: { relation: DEFAULT_CONFLICT_RELATIONS },
      pageSize: DEFAULT_CONFLICT_PAGE_SIZE,
      mapper: normalizeConflict,
    })
      .then(setProjectConflicts)
      .catch(e => setProjectConflictsError(e?.response?.data?.error || e.message))
      .finally(() => setProjectConflictsLoading(false));
  }, [fetchAllPages, id, normalizeConflict]);

  const scheduleProjectConflictsReload = useCallback(() => {
    if (conflictsReloadTimerRef.current) {
      return;
    }

    conflictsReloadTimerRef.current = setTimeout(() => {
      conflictsReloadTimerRef.current = null;
      loadProjectConflicts();
    }, 300);
  }, [loadProjectConflicts]);

  useEffect(() => {
    const previousStatuses = fileProcessingStatusRef.current;
    const nextStatuses = new Map();
    let shouldReloadRequirements = false;
    let shouldReloadProjectConflicts = false;
    let completedFile = null;

    files.forEach((file) => {
      if (!file?.id) {
        return;
      }

      const fileId = file.id;
      const status = normalizeProcessingStatus(file.processingStatus) || (file.analyzed ? 'DONE' : 'QUEUED');
      const previousStatus = previousStatuses.get(fileId);
      nextStatuses.set(fileId, status);

      if (!previousStatus || previousStatus === status) {
        return;
      }

      if ((previousStatus === 'QUEUED' || previousStatus === 'REQUIREMENTS')
        && ['WAITING_CONFLICTS', 'CONFLICTS', 'DONE'].includes(status)) {
        shouldReloadRequirements = true;
      }

      if ((previousStatus === 'WAITING_CONFLICTS' || previousStatus === 'CONFLICTS')
        && (status === 'DONE' || status === 'ERROR')) {
        shouldReloadRequirements = true;
        shouldReloadProjectConflicts = true;
      }

      if (status === 'DONE') {
        completedFile = {
          fileId,
          fileName: file.originalName || 'Файл',
          requirementsCount: fileRequirementIdsRef.current.get(fileId)?.size || 0,
        };
      }
    });

    fileProcessingStatusRef.current = nextStatuses;

    if (completedFile) {
      setLastCompletedFile(completedFile);
    }
    if (shouldReloadRequirements) {
      reloadRequirements();
    }
    if (shouldReloadProjectConflicts) {
      scheduleProjectConflictsReload();
    }
  }, [files, reloadRequirements, scheduleProjectConflictsReload]);

  const removeConflictFromCaches = useCallback((conflict) => {
    if (!conflict) {
      return;
    }

    const affectedIds = new Set();
    const collectId = (value) => {
      if (value) {
        affectedIds.add(value);
      }
    };

    collectId(conflict.requirementStableId);
    collectId(conflict.requirementId);
    collectId(conflict.targetRequirementId);
    collectId(conflict.stableId);
    collectId(resolveRequirementId(conflict));
    if (conflict.left) {
      collectId(conflict.left.stableId);
      collectId(conflict.left.id);
    }
    if (conflict.right) {
      collectId(conflict.right.stableId);
      collectId(conflict.right.id);
    }

    if (conflict.id) {
      Object.entries(conflictsCacheRef.current).forEach(([rid, list]) => {
        if (!Array.isArray(list)) {
          return;
        }
        const filtered = list.filter(item => item?.id !== conflict.id);
        if (filtered.length !== list.length) {
          if (filtered.length > 0) {
            conflictsCacheRef.current[rid] = filtered;
          } else {
            delete conflictsCacheRef.current[rid];
          }
          affectedIds.add(rid);
        }
      });
    }

    if (affectedIds.size > 0) {
      setConflictMap(prev => {
        const next = { ...prev };
        affectedIds.forEach((rid) => {
          if (next[rid] != null) {
            const nextValue = Math.max(0, (next[rid] || 0) - 1);
            if (nextValue <= 0) {
              delete next[rid];
            } else {
              next[rid] = nextValue;
            }
          }
        });
        return next;
      });
    }
  }, []);

  const closeIgnoreDialog = useCallback(() => {
    if (ignoreLoading || isTestGenerationBusy) {
      return;
    }
    setIgnoreDialog({ open: false, type: null, conflict: null });
  }, [ignoreLoading, isTestGenerationBusy]);

  const handleIgnoreSingle = useCallback(async (conflict) => {
    if (!conflict || !conflict.id || ignoreLoading || isTestGenerationBusy) {
      return;
    }

    setProjectConflictsError(null);
    setIgnoreLoading(true);
    try {
      await apiClient.delete(`/api/projects/${id}/conflicts/${conflict.id}`);
      setProjectConflicts(prev => prev.filter(item => item?.id !== conflict.id));
      removeConflictFromCaches(conflict);
      setIgnoreDialog({ open: false, type: null, conflict: null });
    } catch (e) {
      setProjectConflictsError(e?.response?.data?.error || e.message);
    } finally {
      setIgnoreLoading(false);
    }
  }, [apiClient, id, ignoreLoading, isTestGenerationBusy, removeConflictFromCaches]);

  const handleIgnoreAll = useCallback(async () => {
    if (ignoreLoading || isTestGenerationBusy || llmInteractionLocked) {
      return;
    }

    setProjectConflictsError(null);
    setIgnoreLoading(true);
    try {
      await apiClient.post(`/api/projects/${id}/conflicts/ignore-all`);
      conflictsCacheRef.current = {};
      setConflictMap({});
      setProjectConflicts([]);
      setIgnoreDialog({ open: false, type: null, conflict: null });
    } catch (e) {
      setProjectConflictsError(e?.response?.data?.error || e.message);
    } finally {
      setIgnoreLoading(false);
    }
  }, [
    apiClient,
    id,
    ignoreLoading,
    llmInteractionLocked,
    isTestGenerationBusy,
  ]);

  const handleIgnoreAllClick = useCallback(() => {
    if (ignoreLoading || isTestGenerationBusy || llmInteractionLocked) {
      return;
    }
    if (projectConflicts && projectConflicts.length > 0) {
      setIgnoreDialog({ open: true, type: 'all', conflict: null });
      return;
    }
    handleIgnoreAll();
  }, [handleIgnoreAll, ignoreLoading, isTestGenerationBusy, llmInteractionLocked, projectConflicts]);

  const handleIgnoreConflictClick = useCallback((conflict) => {
    if (ignoreLoading || isTestGenerationBusy) {
      return;
    }
    setIgnoreDialog({ open: true, type: 'single', conflict });
  }, [ignoreLoading, isTestGenerationBusy]);

  useEffect(() => {
    loadProjectConflicts();
  }, [loadProjectConflicts]);

  const fetchConflictsFallback = useCallback((fileId) => {
    const relatedReqIds = Object.entries(requirementToFileRef.current)
      .filter(([, fid]) => fid === fileId)
      .map(([rid]) => rid);

    if (relatedReqIds.length === 0) {
      return;
    }

    Promise.all(
      relatedReqIds.map(rid =>
        fetchRequirementConflicts(rid)
          .then(items => [rid, items])
          .catch(() => [rid, null])
      )
    ).then(pairs => {
      const { open, reqId } = confModalStateRef.current;
      const updates = {};

      pairs.forEach(([rid, items]) => {
        if (!Array.isArray(items)) {
          return;
        }

        conflictsCacheRef.current[rid] = items;
        updates[rid] = items.length;

        if (open && reqId === rid) {
          setConfLoading(false);
          setConfItems(items);
        }
      });

      if (Object.keys(updates).length > 0) {
        setConflictMap(prev => ({
          ...prev,
          ...updates,
        }));
      }
    }).finally(() => {
      scheduleProjectConflictsReload();
    });
  }, [fetchRequirementConflicts, scheduleProjectConflictsReload]);

  const handleIncomingConflicts = useCallback((fileId, incoming) => {
    const list = Array.isArray(incoming) ? incoming : (incoming != null ? [incoming] : []);
    if (list.length === 0) {
      return;
    }

    const normalized = list
      .map(normalizeConflict)
      .filter(item => item);

    if (normalized.length === 0) {
      return;
    }

    const grouped = normalized.reduce((acc, conflict) => {
      const requirementId = resolveRequirementId(conflict);

      if (!requirementId) {
        return acc;
      }

      requirementToFileRef.current[requirementId] = fileId;

      if (!acc[requirementId]) {
        acc[requirementId] = [];
      }

      acc[requirementId].push(conflict);
      return acc;
    }, {});

    const requirementIds = Object.keys(grouped);
    if (requirementIds.length === 0) {
      return;
    }

    requirementIds.forEach((rid) => {
      const prevList = conflictsCacheRef.current[rid] || [];
      conflictsCacheRef.current[rid] = [...prevList, ...grouped[rid]];
    });

    setConflictMap(prev => {
      const next = { ...prev };
      requirementIds.forEach((rid) => {
        const prevValue = prev[rid] || 0;
        next[rid] = prevValue + grouped[rid].length;
      });
      return next;
    });

    const { open, reqId } = confModalStateRef.current;
    if (open && reqId && grouped[reqId]) {
      setConfLoading(false);
      setConfItems(prev => [...prev, ...grouped[reqId]]);
    }

    scheduleProjectConflictsReload();
  }, [normalizeConflict, resolveRequirementId, scheduleProjectConflictsReload]);

  const handleIncomingValidations = useCallback((fileId, updates) => {
    const items = Array.isArray(updates) ? updates : (updates != null ? [updates] : []);
    if (items.length === 0) {
      return;
    }

    const updatesMap = new Map();
    items.forEach((raw) => {
      if (!raw || typeof raw !== 'object') {
        return;
      }

      const requirementId = raw.requirementId
        || raw.requirementStableId
        || raw.id
        || raw.stableId;

      if (!requirementId) {
        return;
      }

      if (typeof raw.fileId === 'string' && raw.fileId.length > 0) {
        requirementToFileRef.current[requirementId] = raw.fileId;
      }

      const validationPayload = (raw.validation && typeof raw.validation === 'object')
        ? raw.validation
        : null;

      if (validationPayload) {
        const normalizedIssues = Array.isArray(validationPayload.issues)
          ? validationPayload.issues
            .map((issue) => (typeof issue === 'string' ? issue.trim() : ''))
            .filter((issue) => issue.length > 0)
          : [];
        const suggestion = typeof validationPayload.suggestedRewrite === 'string'
          ? validationPayload.suggestedRewrite.trim()
          : '';

        updatesMap.set(requirementId, {
          ...validationPayload,
          issues: normalizedIssues,
          suggestedRewrite: suggestion.length > 0 ? suggestion : null,
        });
      }
    });

    if (updatesMap.size === 0) {
      return;
    }

    setReqPage((prev) => {
      if (!prev || !Array.isArray(prev.content) || prev.content.length === 0) {
        return prev;
      }

      let changed = false;
      const nextContent = prev.content.map((req) => {
        const rid = req?.stableId || req?.id;
        if (!rid || !updatesMap.has(rid)) {
          return req;
        }

        const validation = updatesMap.get(rid);
        if (!validation) {
          return req;
        }

        const nextReq = { ...req };
        const mergedValidation = {
          ...(typeof nextReq.validation === 'object' && nextReq.validation ? nextReq.validation : {}),
          ...validation,
        };
        mergedValidation.issues = Array.isArray(validation.issues) ? [...validation.issues] : [];
        nextReq.validation = mergedValidation;
        nextReq.validationIssues = mergedValidation.issues;
        nextReq.issues = mergedValidation.issues;
        nextReq.suggestedRewrite = mergedValidation.suggestedRewrite || null;

        ['atomicity', 'unambiguous', 'testable', 'complete'].forEach((key) => {
          const mergedValue = mergedValidation[key];
          if (typeof mergedValue === 'boolean') {
            nextReq[key] = mergedValue;
            return;
          }
          const prevDirect = req[key];
          nextReq[key] = typeof prevDirect === 'boolean' ? prevDirect : null;
        });

        changed = true;
        return nextReq;
      });

      if (!changed) {
        return prev;
      }

      return { ...prev, content: nextContent };
    });
  }, []);

  const startConflictsSubscription = useCallback((fileId) => {
    if (!fileId || conflictSubscriptionsRef.current.has(fileId)) {
      return;
    }

    setProcessingStages(prev => ({
      ...prev,
      [fileId]: 'conflicts',
    }));
    patchFileRuntimeState(fileId, {
      processingStatus: 'CONFLICTS',
      processingProgress: 100,
      analysisError: null,
    });

    const unsubscribe = subscribeToConflicts(fileId, {
      onConflict: (conflict) => handleIncomingConflicts(fileId, conflict),
      onConflictPage: (conflicts) => handleIncomingConflicts(fileId, conflicts),
      onValidation: (payload) => handleIncomingValidations(fileId, payload),
      onComplete: () => {
        conflictSubscriptionsRef.current.delete(fileId);
        if (confModalStateRef.current.open) {
          setConfLoading(false);
        }
        setLastCompletedFile({
          fileId,
          fileName: files.find(item => item?.id === fileId)?.originalName || 'Файл',
          requirementsCount: fileRequirementIdsRef.current.get(fileId)?.size || 0,
        });
        setProcessingStages(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        patchFileRuntimeState(fileId, {
          processingStatus: 'DONE',
          processingProgress: 100,
          analysisError: null,
          analyzed: true,
        });
      },
      onError: () => {
        conflictSubscriptionsRef.current.delete(fileId);
        if (confModalStateRef.current.open) {
          setConfLoading(false);
        }
        setProcessingStages(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        loadProjectFiles({ silent: true });
        fetchConflictsFallback(fileId);
        reloadRequirements();
      },
    });

    conflictSubscriptionsRef.current.set(fileId, unsubscribe);
  }, [fetchConflictsFallback, files, handleIncomingConflicts, handleIncomingValidations, loadProjectFiles, patchFileRuntimeState, reloadRequirements]);

  const startProcessingSubscription = useCallback((fileId) => {
    if (!fileId || processingSubscriptionsRef.current.has(fileId)) {
      return;
    }

    setProcessingStages(prev => ({
      ...prev,
      [fileId]: 'requirements',
    }));
    patchFileRuntimeState(fileId, {
      processingStatus: 'REQUIREMENTS',
      analysisError: null,
    });

    const unsubscribe = subscribeToFileProcessing(fileId, {
      onProgress: (percent) => {
        if (percent == null) {
          return;
        }
        const normalized = Number.isFinite(percent) ? Math.round(percent) : null;
        if (normalized == null) {
          return;
        }
        setProcessingProgress(prev => ({
          ...prev,
          [fileId]: normalized,
        }));
        patchFileRuntimeState(fileId, {
          processingStatus: 'REQUIREMENTS',
          processingProgress: normalized,
          analysisError: null,
        });
      },
      onRequirements: (items) => handleIncomingRequirements(fileId, items),
      onComplete: () => {
        setProcessingProgress(prev => ({
          ...prev,
          [fileId]: 100,
        }));
        processingSubscriptionsRef.current.delete(fileId);
        patchFileRuntimeState(fileId, {
          processingStatus: 'WAITING_CONFLICTS',
          processingProgress: 100,
          analysisError: null,
          analyzed: true,
        });
        startConflictsSubscription(fileId);
        if (isAdmin) {
          loadLlmDebug();
        }
      },
      onError: () => {
        processingSubscriptionsRef.current.delete(fileId);
        setProcessingProgress(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        setProcessingStages(prev => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        patchFileRuntimeState(fileId, {
          processingStatus: 'ERROR',
          analysisError: 'Ошибка при извлечении требований.',
        });
        loadProject();
        loadReq(reqPageRef.current.page ?? 0, reqPageRef.current.size ?? 20);
      },
    });

    processingSubscriptionsRef.current.set(fileId, unsubscribe);
  }, [
    handleIncomingRequirements,
    isAdmin,
    loadLlmDebug,
    loadProject,
    loadReq,
    patchFileRuntimeState,
    startConflictsSubscription,
  ]);

  useEffect(() => {
    const nextStages = {};
    const nextProgress = {};
    const requirementsActive = new Set();
    const conflictsActive = new Set();

    files.forEach((file) => {
      if (!file?.id) {
        return;
      }
      const status = normalizeProcessingStatus(file.processingStatus) || (file.analyzed ? 'DONE' : 'QUEUED');
      const numericProgress = Number(file.processingProgress);
      const hasProgress = Number.isFinite(numericProgress);

      if (status === 'QUEUED' || status === 'REQUIREMENTS') {
        nextStages[file.id] = 'requirements';
        nextProgress[file.id] = hasProgress ? Math.max(0, Math.min(100, Math.round(numericProgress))) : 0;
        requirementsActive.add(file.id);
        return;
      }

      if (status === 'CONFLICTS') {
        nextStages[file.id] = 'conflicts';
        nextProgress[file.id] = 100;
        conflictsActive.add(file.id);
        return;
      }

      if (status === 'WAITING_CONFLICTS' || status === 'DONE') {
        nextProgress[file.id] = 100;
        return;
      }

      if (status === 'ERROR' && hasProgress) {
        nextProgress[file.id] = Math.max(0, Math.min(100, Math.round(numericProgress)));
      }
    });

    setProcessingStages(nextStages);
    setProcessingProgress(nextProgress);

    processingSubscriptionsRef.current.forEach((unsubscribe, fileId) => {
      if (requirementsActive.has(fileId)) {
        return;
      }
      try { unsubscribe(); } catch (e) { /* noop */ }
      processingSubscriptionsRef.current.delete(fileId);
    });

    conflictSubscriptionsRef.current.forEach((unsubscribe, fileId) => {
      if (conflictsActive.has(fileId)) {
        return;
      }
      try { unsubscribe(); } catch (e) { /* noop */ }
      conflictSubscriptionsRef.current.delete(fileId);
    });

    requirementsActive.forEach((fileId) => {
      if (!processingSubscriptionsRef.current.has(fileId)) {
        startProcessingSubscription(fileId);
      }
    });

    conflictsActive.forEach((fileId) => {
      if (!conflictSubscriptionsRef.current.has(fileId)) {
        startConflictsSubscription(fileId);
      }
    });
  }, [files, startConflictsSubscription, startProcessingSubscription]);

  const clearFileConflictsState = useCallback((fileId) => {
    if (!fileId) {
      return;
    }

    const affectedRequirementIds = Object.entries(requirementToFileRef.current)
      .filter(([, sourceFileId]) => sourceFileId === fileId)
      .map(([rid]) => rid);

    if (!affectedRequirementIds.length) {
      return;
    }

    affectedRequirementIds.forEach((rid) => {
      delete conflictsCacheRef.current[rid];
    });

    setConflictMap((prev) => {
      let changed = false;
      const next = { ...prev };
      affectedRequirementIds.forEach((rid) => {
        if (next[rid] !== undefined) {
          delete next[rid];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [setConflictMap]);

  const openRequirementEdit = (req) => {
    if (!req) return;
    const rid = req?.stableId || req?.id;
    if (!rid) return;

    setEditDialog({
      open: true,
      reqId: rid,
      text: getRequirementText(req),
      enabled: req?.enabled !== false, // default to true if undefined
    });
    setEditError(null);
  };

  const closeRequirementEdit = () => {
    if (editSaving) {
      return;
    }
    setEditDialog(prev => ({ ...prev, open: false }));
    setEditError(null);
  };

  const handleRequirementFieldChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setEditDialog(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveRequirementEdit = () => {
    if (!editDialog.reqId) {
      return;
    }

    setEditSaving(true);
    setEditError(null);

    const sanitize = (value) => {
      if (value == null) return null;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    };

    const payload = {
      text: sanitize(editDialog.text),
      enabled: editDialog.enabled,
    };

    apiClient.put(`/api/projects/${id}/requirements/${editDialog.reqId}`, payload)
      .then(res => {
        const updated = res?.data ? normalizeRequirement(res.data) : null;
        const rid = editDialog.reqId;

        if (updated && rid) {
          const fileId = updated.fileId || requirementToFileRef.current[rid];
          if (fileId) {
            requirementToFileRef.current[rid] = fileId;
          }

          delete conflictsCacheRef.current[rid];
          setConflictMap(prev => {
            const next = { ...prev };
            delete next[rid];
            return next;
          });

          setReqPage(prev => {
            if (!Array.isArray(prev.content)) {
              return prev;
            }
            const nextContent = prev.content.map(item => {
              const itemId = item?.stableId || item?.id;
              if (itemId === rid) {
                return { ...item, ...updated };
              }
              return item;
            });
            return { ...prev, content: nextContent };
          });
        }

        setEditDialog({
          open: false,
          reqId: null,
          text: '',
          enabled: true,
        });
      })
      .catch(e => {
        setEditError(e?.response?.data?.error || e.message);
      })
      .finally(() => {
        setEditSaving(false);
      });
  };

  const handleRequirementFeedbackChange = useCallback((req, targetFeedback) => {
    const rid = req?.stableId || req?.id;
    if (!rid || !targetFeedback) {
      return;
    }

    const normalizedTarget = normalizeRequirementFeedback(targetFeedback);
    if (!normalizedTarget) {
      return;
    }

    const currentFeedback = normalizeRequirementFeedback(req?.userFeedback || req?.user_feedback);
    const nextFeedback = currentFeedback === normalizedTarget ? null : normalizedTarget;

    setFeedbackSavingMap((prev) => ({ ...prev, [rid]: true }));
    setReqErr(null);

    apiClient.put(`/api/projects/${id}/requirements/${rid}`, {
      userFeedback: nextFeedback ?? '',
    })
      .then((res) => {
        const updated = res?.data ? normalizeRequirement(res.data) : null;
        setReqPage((prev) => {
          if (!Array.isArray(prev.content)) {
            return prev;
          }
          const nextContent = prev.content.map((item) => {
            const itemId = item?.stableId || item?.id;
            if (itemId !== rid) {
              return item;
            }
            if (updated) {
              return { ...item, ...updated };
            }
            return {
              ...item,
              userFeedback: nextFeedback,
            };
          });
          return { ...prev, content: nextContent };
        });
      })
      .catch((e) => {
        setReqErr(e?.response?.data?.error || e.message);
      })
      .finally(() => {
        setFeedbackSavingMap((prev) => {
          const next = { ...prev };
          delete next[rid];
          return next;
        });
      });
  }, [apiClient, id]);

  const openApplyRewriteDialog = (req, suggestedText) => {
    const rid = req?.stableId || req?.id;
    const trimmed = typeof suggestedText === 'string' ? suggestedText.trim() : '';

    if (!rid || trimmed.length === 0) {
      return;
    }

    setApplyRewriteDialog({
      open: true,
      reqId: rid,
      seqNumber: req?.seqNumber ?? null,
      suggestedText,
    });
    setApplyRewriteError(null);
  };

  const resolveAutoRewriteSuggestion = useCallback((req, validation = null) => {
    const normalizedValidation = validation && typeof validation === 'object'
      ? validation
      : normalizeRequirementValidation(req);

    const candidateSuggestion = typeof normalizedValidation?.suggestedRewrite === 'string'
      ? sanitizeSuggestedRewrite(normalizedValidation.suggestedRewrite)
      : '';

    if (typeof candidateSuggestion === 'string' && candidateSuggestion.length > 0
      && shouldUseSuggestionAsRewrite(candidateSuggestion)) {
      return candidateSuggestion;
    }
    return null;
  }, []);

  const openAutoRewriteDialog = useCallback(async (req, suggestionOverride = null) => {
    if (!req) {
      return;
    }

    const rid = req?.stableId || req?.id;
    if (!rid) {
      return;
    }

    const suggested = typeof suggestionOverride === 'string' && suggestionOverride.trim().length > 0
      ? suggestionOverride.trim()
      : resolveAutoRewriteSuggestion(req);

    if (!suggested) {
      setApplyRewriteError(null);
      autoRewritePrefetchInFlightRef.current.add(rid);
      setAutoRewriteLoadingMap((prev) => ({ ...prev, [rid]: true }));
      try {
        const response = await apiClient.post(`/api/projects/${id}/requirements/${rid}/rewrite-suggestion`);
        const updated = response?.data ? normalizeRequirement(response.data) : null;
        let effectiveReq = req;

        if (updated) {
          effectiveReq = { ...req, ...updated };
          setReqPage((prev) => {
            if (!Array.isArray(prev.content)) {
              return prev;
            }
            const nextContent = prev.content.map((item) => {
              const itemId = item?.stableId || item?.id;
              return itemId === rid ? { ...item, ...updated } : item;
            });
            return { ...prev, content: nextContent };
          });
        }

        const resolved = resolveAutoRewriteSuggestion(effectiveReq, normalizeRequirementValidation(effectiveReq));
        if (resolved) {
          openApplyRewriteDialog(effectiveReq, resolved);
        } else {
          setApplyRewriteError('LLM не вернула готовую формулировку. Попробуйте снова.');
        }
      } catch (e) {
        setApplyRewriteError(e?.response?.data?.error || e.message);
      } finally {
        autoRewritePrefetchInFlightRef.current.delete(rid);
        autoRewritePrefetchAttemptedRef.current.add(rid);
        setAutoRewriteLoadingMap((prev) => {
          const next = { ...prev };
          delete next[rid];
          return next;
        });
      }
      return;
    }

    openApplyRewriteDialog(req, suggested);
  }, [apiClient, id, resolveAutoRewriteSuggestion]);

  const closeApplyRewriteDialog = () => {
    const activeReqId = applyRewriteDialog.reqId;
    if (activeReqId && applyRewriteLoadingMap[activeReqId]) {
      return;
    }

    setApplyRewriteDialog({
      open: false,
      reqId: null,
      seqNumber: null,
      suggestedText: '',
    });
    setApplyRewriteError(null);
  };

  const handleApplySuggestedRewrite = () => {
    const { reqId, suggestedText } = applyRewriteDialog;
    const trimmed = typeof suggestedText === 'string' ? suggestedText.trim() : '';

    if (!reqId || trimmed.length === 0) {
      closeApplyRewriteDialog();
      return;
    }

    setApplyRewriteError(null);
    setApplyRewriteLoadingMap(prev => ({ ...prev, [reqId]: true }));

    const payload = {
      text: trimmed,
      suggestedRewrite: null,
    };

    apiClient.put(`/api/projects/${id}/requirements/${reqId}`, payload)
      .then((res) => {
        const updated = res?.data ? normalizeRequirement(res.data) : null;

        if (updated) {
          const fileId = updated.fileId || requirementToFileRef.current[reqId];
          if (fileId) {
            requirementToFileRef.current[reqId] = fileId;
          }

          delete conflictsCacheRef.current[reqId];
          setConflictMap(prev => {
            const next = { ...prev };
            delete next[reqId];
            return next;
          });
        }

        setReqPage(prev => {
          if (!Array.isArray(prev.content)) {
            return prev;
          }

          const nextContent = prev.content.map(item => {
            const itemId = item?.stableId || item?.id;
            if (itemId === reqId) {
              if (updated) {
                return { ...item, ...updated };
              }
              return { ...item, text: trimmed, suggestedRewrite: null };
            }
            return item;
          });

          return { ...prev, content: nextContent };
        });

        setApplyRewriteDialog({
          open: false,
          reqId: null,
          seqNumber: null,
          suggestedText: '',
        });
      })
      .catch((e) => {
        setApplyRewriteError(e?.response?.data?.error || e.message);
      })
      .finally(() => {
        setApplyRewriteLoadingMap(prev => {
          const next = { ...prev };
          delete next[reqId];
          return next;
        });
      });
  };

  const openFileDialog = () => {
    if (llmInteractionLocked) {
      return;
    }
    setUploadSourceDialogOpen(true);
    apiClient.get('/api/ingest/confluence/status')
      .then((response) => setConfluenceConnected(response?.data?.connected === true))
      .catch(() => setConfluenceConnected(false));
  };

  const openComputerFileDialog = () => {
    if (llmInteractionLocked) {
      return;
    }
    setUploadSourceDialogOpen(false);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const openConfluenceDialog = () => {
    if (llmInteractionLocked) {
      return;
    }
    setUploadSourceDialogOpen(false);
    setConfluenceDialogOpen(true);
    setConfluenceError(null);
    setConfluenceSuccess(null);
  };

  const closeConfluenceDialog = () => {
    if (confluenceLoading) {
      return;
    }
    setConfluenceDialogOpen(false);
    setConfluenceUrl('');
    setConfluenceError(null);
  };

  const handleAddConfluenceFile = async () => {
    const url = confluenceUrl.trim();
    if (!url || confluenceLoading || llmInteractionLocked) {
      return;
    }
    setConfluenceLoading(true);
    setConfluenceError(null);
    setConfluenceSuccess(null);
    try {
      const response = await apiClient.post('/api/ingest/confluence', null, { params: { url, projectId: id } });
      if (response?.data) {
        setConfluenceTargets((prev) => {
          const next = Array.isArray(prev) ? prev : [];
          const withoutCurrent = next.filter((item) => item?.id !== response.data.id);
          return [...withoutCurrent, response.data];
        });
      }
      setConfluenceSuccess('Страница Confluence добавлена в обработку.');
      setConfluenceUrl('');
      setConfluenceDialogOpen(false);
    } catch (e) {
      if (e?.response?.status === 428) {
        setConfluenceError('Для использования страниц из Confluence надо сначала подключить Confluence в настройках системы.');
      } else {
        setConfluenceError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Не удалось добавить страницу Confluence.');
      }
    } finally {
      setConfluenceLoading(false);
    }
  };

  const handleDeleteConfluenceTarget = async (target) => {
    if (!target?.id || deletingConfluenceTargetId || !window.confirm(`Удалить страницу «${target.title || target.url}» из проекта?`)) return;
    setDeletingConfluenceTargetId(target.id);
    try {
      await apiClient.delete(`/api/ingest/confluence/target/${encodeURIComponent(target.id)}`, { params: { projectId: id } });
      setConfluenceTargets((prev) => prev.filter((item) => item?.id !== target.id));
      reloadRequirements();
    } catch (e) {
      setConfluenceError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Не удалось удалить страницу Confluence.');
    } finally {
      setDeletingConfluenceTargetId(null);
    }
  };

  const uploadFilesWithMode = useCallback((selectedFiles, comparisonMode = 'INCLUDE_PREVIOUS') => {
    if (llmInteractionLocked) {
      return;
    }
    const filesList = Array.isArray(selectedFiles) ? selectedFiles : [];
    if (filesList.length === 0) {
      return;
    }

    setLastCompletedFile(null);
    setUploading(true);
    setUploadProgress({ done: 0, total: filesList.length });
    const effectiveComparisonMode = comparisonMode === 'INDEPENDENT' ? 'INDEPENDENT' : 'INCLUDE_PREVIOUS';

    // грузим по одному (бэкенд принимает один файл за раз)
    const uploadNext = (idx) => {
      if (idx >= filesList.length) {
        setUploading(false);
        setUploadProgress({ done: filesList.length, total: filesList.length });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      const f = filesList[idx];
      const form = new FormData();
      form.append('file', f);
      apiClient.post(`/api/projects/${id}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { comparisonMode: effectiveComparisonMode },
      })
        .then(res => {
          const uploadedFile = res?.data;
          const fileId = uploadedFile?.id;

          if (uploadedFile) {
            let added = false;
            setFiles(prev => {
              const list = Array.isArray(prev) ? [...prev] : [];
              const existingIdx = list.findIndex(item => item.id === uploadedFile.id);
              if (existingIdx >= 0) {
                list[existingIdx] = { ...list[existingIdx], ...uploadedFile };
              } else {
                list.unshift(uploadedFile);
                added = true;
              }
              return list;
            });

            if (added) {
              setProject(prev => prev ? ({
                ...prev,
                filesCount: (prev.filesCount || 0) + 1,
              }) : prev);
            }
          }

          if (fileId) {
            setProcessingProgress(prev => ({
              ...prev,
              [fileId]: 0,
            }));
            setProcessingStages(prev => ({
              ...prev,
              [fileId]: 'requirements',
            }));
            patchFileRuntimeState(fileId, {
              processingStatus: 'QUEUED',
              processingProgress: 0,
              analysisError: null,
            });

            const existing = processingSubscriptionsRef.current.get(fileId);
            if (existing) {
              try { existing(); } catch (e2) { /* noop */ }
              processingSubscriptionsRef.current.delete(fileId);
            }
            startProcessingSubscription(fileId);
          } else {
            loadReq(reqPageRef.current.page ?? 0, reqPageRef.current.size ?? 20);
          }

          setUploadProgress(prev => ({ done: prev.done + 1, total: prev.total }));
          uploadNext(idx + 1);
        })
        .catch(e => {
          setErr(e?.response?.data?.error || e.message);
          setUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        });
    };
    uploadNext(0);
  }, [apiClient, id, llmInteractionLocked, loadReq, patchFileRuntimeState, startProcessingSubscription]);

  const closeUploadModeDialog = useCallback(() => {
    if (uploading) {
      return;
    }
    setUploadModeDialogOpen(false);
    setPendingUploadFiles([]);
  }, [uploading]);

  const confirmUploadWithMode = useCallback(() => {
    if (llmInteractionLocked) {
      return;
    }
    if (!Array.isArray(pendingUploadFiles) || pendingUploadFiles.length === 0) {
      setUploadModeDialogOpen(false);
      return;
    }
    const filesToUpload = [...pendingUploadFiles];
    setPendingUploadFiles([]);
    setUploadModeDialogOpen(false);
    uploadFilesWithMode(filesToUpload, uploadComparisonMode);
  }, [llmInteractionLocked, pendingUploadFiles, uploadComparisonMode, uploadFilesWithMode]);

  const handleFilesSelected = (e) => {
    if (llmInteractionLocked) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const filesList = e.target.files;
    if (!filesList || filesList.length === 0) return;
    const selectedFiles = Array.from(filesList);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const hasExistingFiles = Array.isArray(files) && files.length > 0;
    if (hasExistingFiles) {
      setPendingUploadFiles(selectedFiles);
      setUploadComparisonMode('INCLUDE_PREVIOUS');
      setUploadModeDialogOpen(true);
      return;
    }

    uploadFilesWithMode(selectedFiles, 'INCLUDE_PREVIOUS');
  };

  const handleManualConflictsCheck = useCallback(async (fileId) => {
    if (!fileId || llmInteractionLocked) {
      return;
    }

    setErr(null);
    setLastCompletedFile(prev => (prev?.fileId === fileId ? null : prev));
    clearFileConflictsState(fileId);
    setProcessingStages(prev => ({
      ...prev,
      [fileId]: 'conflicts',
    }));
    patchFileRuntimeState(fileId, {
      processingStatus: 'CONFLICTS',
      processingProgress: 100,
      analysisError: null,
    });

    try {
      await apiClient.post(`/api/projects/${id}/files/${fileId}/conflicts/recheck`);
      startConflictsSubscription(fileId);
    } catch (error) {
      const message = error?.response?.data?.error
        || error?.message
        || 'Не удалось запустить проверку конфликтов.';
      setErr(message);
      setProcessingStages(prev => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
      patchFileRuntimeState(fileId, {
        processingStatus: 'ERROR',
        analysisError: message,
      });
    }
  }, [apiClient, clearFileConflictsState, id, llmInteractionLocked, patchFileRuntimeState, setErr, startConflictsSubscription]);

  const handleGoToRequirements = useCallback(() => {
    setProjectTab('requirements');
    if (requirementsSectionRef.current && typeof requirementsSectionRef.current.scrollIntoView === 'function') {
      requirementsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleRecheckLastCompletedConflicts = useCallback(() => {
    if (llmInteractionLocked) {
      return;
    }
    const fileId = lastCompletedFile?.fileId;
    if (!fileId) {
      return;
    }
    handleManualConflictsCheck(fileId);
  }, [handleManualConflictsCheck, lastCompletedFile, llmInteractionLocked]);

  const downloadFile = useCallback(async (file, mode = 'original') => {
    if (!file || !file.id) {
      return;
    }

    const isEdited = mode === 'edited';
    const editedContentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const endpoint = isEdited
      ? `/api/projects/${id}/files/${file.id}/download/edited`
      : `/api/projects/${id}/files/${file.id}/download`;
    const setError = isEdited ? setEditedFileDownloadError : setFileDownloadError;

    setDownloadingFileIds(prev => ({
      ...prev,
      [file.id]: true,
    }));
    setError(null);
    if (isEdited) {
      setEditedFileDownloadSuccess(null);
    }

    try {
      const response = await apiClient.get(endpoint, {
        responseType: 'blob',
      });

      const responseContentType = response?.headers?.['content-type'] || (response?.data instanceof Blob ? response.data.type : null);
      const fallbackType = responseContentType || (isEdited ? editedContentType : file.contentType) || 'application/octet-stream';
      const blobData = response?.data instanceof Blob
        ? (response.data.type ? response.data : new Blob([response.data], { type: fallbackType }))
        : new Blob([response?.data ?? new ArrayBuffer(0)], { type: fallbackType });
      const objectUrl = window.URL.createObjectURL(blobData);

      const disposition = response?.headers?.['content-disposition'];
      let filename = file.originalName || 'project-file';
      if (typeof disposition === 'string') {
        const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
        if (match) {
          const raw = match[1] || match[2];
          try {
            filename = decodeURIComponent(raw);
          } catch (e) {
            filename = raw;
          }
        }
      }

      if (isEdited && (!filename || filename === file.originalName || filename === 'project-file')) {
        const withoutExt = (file.originalName || 'requirements').replace(/\.[^.]+$/, '');
        filename = `${withoutExt}-edited.docx`;
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || file.originalName || 'project-file';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      if (isEdited) {
        setEditedFileDownloadSuccess(`Файл «${filename || file.originalName || 'requirements-edited.docx'}» успешно выгружен.`);
      }
    } catch (downloadError) {
      const message = downloadError?.response?.data?.error
        || downloadError?.message
        || (isEdited
          ? 'Не удалось скачать документ с подсветкой. Попробуйте ещё раз.'
          : 'Не удалось скачать исходный файл. Попробуйте ещё раз.');
      setError(message);
    } finally {
      setDownloadingFileIds(prev => {
        const next = { ...prev };
        delete next[file.id];
        return next;
      });
    }
  }, [apiClient, id]);

  const handleFileDownload = useCallback((file) => downloadFile(file, 'original'), [downloadFile]);

  const handleEditedFileDownload = useCallback((file) => downloadFile(file, 'edited'), [downloadFile]);

  const openDeleteFileDialog = (file) => {
    if (!file) {
      return;
    }
    setDeleteDialog({ open: true, file });
    setDeleteError(null);
  };

  const closeDeleteFileDialog = () => {
    if (deletingFileId) {
      return;
    }
    setDeleteDialog({ open: false, file: null });
    setDeleteError(null);
  };

  const handleConfirmDeleteFile = async () => {
    const targetFileId = deleteDialog?.file?.id;
    if (!targetFileId || deletingFileId) {
      return;
    }

    setDeletingFileId(targetFileId);
    setDeleteError(null);

    try {
      const response = await apiClient.delete(`/api/projects/${id}/files/${targetFileId}`);
      const summary = response?.data || {};

      setFiles(prev => Array.isArray(prev)
        ? prev.filter(item => item?.id !== targetFileId)
        : prev);
      setProject(prev => {
        if (!prev) {
          return prev;
        }
        const nextFilesCount = Math.max(0, (prev.filesCount || 0) - 1);
        const requirementsDeleted = Number.isFinite(summary.requirementsDeleted)
          ? summary.requirementsDeleted
          : 0;
        const nextRequirementsCount = Math.max(
          0,
          (prev.requirementsCount || 0) - requirementsDeleted
        );
        return {
          ...prev,
          filesCount: nextFilesCount,
          requirementsCount: nextRequirementsCount,
        };
      });

      setProcessingProgress(prev => {
        if (!prev || typeof prev !== 'object') {
          return prev;
        }
        const next = { ...prev };
        delete next[targetFileId];
        return next;
      });

      setProcessingStages(prev => {
        if (!prev || typeof prev !== 'object') {
          return prev;
        }
        const next = { ...prev };
        delete next[targetFileId];
        return next;
      });
      fileRequirementIdsRef.current.delete(targetFileId);
      setLastCompletedFile(prev => (prev?.fileId === targetFileId ? null : prev));

      const stopProcessingSubscription = processingSubscriptionsRef.current.get(targetFileId);
      if (stopProcessingSubscription) {
        try { stopProcessingSubscription(); } catch (e) { /* noop */ }
        processingSubscriptionsRef.current.delete(targetFileId);
      }

      const stopConflictSubscription = conflictSubscriptionsRef.current.get(targetFileId);
      if (stopConflictSubscription) {
        try { stopConflictSubscription(); } catch (e) { /* noop */ }
        conflictSubscriptionsRef.current.delete(targetFileId);
      }

      const relatedRequirementIds = Object.entries(requirementToFileRef.current)
        .filter(([, fid]) => fid === targetFileId)
        .map(([rid]) => rid);

      if (relatedRequirementIds.length > 0) {
        relatedRequirementIds.forEach((rid) => {
          delete requirementToFileRef.current[rid];
          delete conflictsCacheRef.current[rid];
        });

        setConflictMap(prev => {
          const next = { ...prev };
          relatedRequirementIds.forEach((rid) => {
            if (Object.prototype.hasOwnProperty.call(next, rid)) {
              delete next[rid];
            }
          });
          return next;
        });
      }

      loadReq(reqPageRef.current.page ?? 0, reqPageRef.current.size ?? 20);
      scheduleProjectConflictsReload();
      setProjectConflicts(prev => Array.isArray(prev)
        ? prev.filter(conflict => {
          const leftFileId = conflict?.left?.fileId || conflict?.left?.sourceFileId;
          const rightFileId = conflict?.right?.fileId || conflict?.right?.sourceFileId;
          const conflictFileId = conflict?.fileId || conflict?.sourceFileId;
          return leftFileId !== targetFileId
            && rightFileId !== targetFileId
            && conflictFileId !== targetFileId;
        })
        : prev);

      setDeleteDialog({ open: false, file: null });
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Не удалось удалить файл.';
      setDeleteError(message);
    } finally {
      setDeletingFileId(null);
    }
  };

  const resolveFileStatus = useCallback((file) => {
    const fileId = file?.id;
    const stageKey = fileId ? processingStages[fileId] : null;
    const rawProgress = fileId ? processingProgress[fileId] : null;
    const progressValue = Number.isFinite(rawProgress)
      ? Math.max(0, Math.min(100, Math.round(rawProgress)))
      : null;
    const knownRequirements = fileId ? (fileRequirementIdsRef.current.get(fileId)?.size || 0) : 0;
    const backendStatus = normalizeProcessingStatus(file?.processingStatus) || (file?.analyzed ? 'DONE' : null);
    const estimateDuration = formatDurationMs(file?.estimatedProcessingMs);
    const sampleCount = Number(file?.processingStatsSamples);
    const hasEstimate = Number.isFinite(sampleCount) && sampleCount >= MIN_ESTIMATE_SAMPLES && Boolean(estimateDuration);

    if (stageKey === 'requirements') {
      const inQueue = backendStatus === 'QUEUED' && (progressValue == null || progressValue === 0);
      return {
        label: inQueue ? 'В очереди' : 'Разбор требований',
        color: 'info',
        busy: true,
        progress: progressValue != null ? progressValue : 0,
        hint: inQueue
          ? 'Файл ожидает запуска обработки.'
          : (
            progressValue != null
              ? `Обработка: ${progressValue}%${hasEstimate ? ` · ~${estimateDuration}` : ''}`
              : 'Извлекаем требования из документа.'
          ),
      };
    }

    if (stageKey === 'conflicts' || backendStatus === 'CONFLICTS') {
      return {
        label: 'Анализ конфликтов',
        color: 'warning',
        busy: true,
        progress: null,
        hint: 'Сверяем требования между собой.',
      };
    }

    if (backendStatus === 'WAITING_CONFLICTS') {
      return {
        label: 'Ожидает конфликтов',
        color: 'primary',
        busy: true,
        progress: null,
        hint: 'Требования извлечены. Ждём запуск анализа конфликтов.',
      };
    }

    if (backendStatus === 'ERROR') {
      return {
        label: 'Ошибка',
        color: 'error',
        busy: false,
        progress: null,
        hint: file?.analysisError || 'Обработка завершилась с ошибкой.',
      };
    }

    if ((progressValue != null && progressValue >= 100) || backendStatus === 'DONE') {
      const durationLabel = formatDurationMs(file?.analysisDurationMs);
      return {
        label: 'Готово',
        color: 'success',
        busy: false,
        progress: null,
        hint: knownRequirements > 0
          ? `Найдено требований: ${knownRequirements}${durationLabel ? ` · ${durationLabel}` : ''}`
          : (durationLabel ? `Обработка заняла ${durationLabel}.` : 'Можно переходить к требованиям.'),
      };
    }

    if (progressValue != null) {
      return {
        label: 'В обработке',
        color: 'primary',
        busy: true,
        progress: progressValue,
        hint: `Обработка: ${progressValue}%`,
      };
    }

    if (backendStatus === 'QUEUED') {
      return {
        label: 'В очереди',
        color: 'default',
        busy: true,
        progress: 0,
        hint: 'Файл ожидает запуска обработки.',
      };
    }

    return {
      label: 'Ожидает действий',
      color: 'default',
      busy: false,
      progress: null,
        hint: 'Добавьте новые файлы или проверьте конфликты вручную.',
    };
  }, [processingProgress, processingStages]);

  return (
    <Stack spacing={3} className="stagger-children">
      {err && <Alert severity="error">{err}</Alert>}
      {llmInteractionLocked && (
        <Alert severity="info">
          Генерируем промты проекта. Пока генерация не завершится, загрузка файлов и запуск LLM-операций недоступны.
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Typography variant="h4">{project ? project.name : 'Проект'}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack spacing={1} sx={{ width: { xs: '100%', md: 420 } }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Chip label={'Файлы: ' + (project ? project.filesCount : 0)} />
                  <Chip label={'Требования: ' + (project ? project.requirementsCount : 0)} />
                  <Chip label={testingTypeLabel(project?.testingType)} color="info" variant="outlined" />
                </Stack>
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleGoToStepTwo}
                    disabled={(project?.requirementsCount || 0) === 0}
                    endIcon={<EastIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {isLoadProject ? 'Шаг 2: Нагрузочные тест-кейсы' : 'Шаг 2: User Stories'}
                  </Button>
                </Stack>
              </Stack>
              <Button
                variant="contained"
                onClick={handleIgnoreAllClick}
                disabled={ignoreLoading || isTestGenerationBusy || llmInteractionLocked}
                sx={{ display: 'none' }}
              >
                {projectConflicts.length > 0
                  ? 'Игнорировать все конфликты и продолжить'
                  : 'Продолжить'}
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <Paper className="surface-glow" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Настройки проекта</Typography>
              {projectSaveError && <Alert severity="error">{projectSaveError}</Alert>}
              {projectSaveSuccess && <Alert severity="success">Изменения сохранены.</Alert>}
              <TextField
                label="Название проекта"
                value={projectForm.name}
                onChange={handleProjectFieldChange('name')}
                disabled={projectSaving || isPromptGenerationBusy}
              />
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => setPromptDrawerOpen(true)}
                  disabled={projectSaving}
                >
                  Настроить промты
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveProject}
                  disabled={projectSaving || isPromptGenerationBusy || !projectForm.name.trim() || hasPromptLengthOverflow}
                >
                  {projectSaving ? 'Сохраняю…' : 'Сохранить'}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Drawer
            anchor="right"
            open={promptDrawerOpen}
            onClose={() => {
              if (projectSaving) return;
              setPromptDrawerOpen(false);
            }}
            PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 2 } }}
          >
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Typography variant="h6">Промты проекта</Typography>
              <Typography variant="body2" color="text.secondary">
                Настройки применяются только к текущему проекту.
              </Typography>
              {promptGenerationError && <Alert severity="error">{promptGenerationError}</Alert>}
              {isPromptGenerationBusy && (
                <Alert severity="info">
                  {promptGenerationJob?.message || 'Генерация промтов в процессе…'}
                </Alert>
              )}
              <Stack direction="row" justifyContent="flex-start" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={handleGeneratePromptsWithLlm}
                  disabled={projectSaving || isPromptGenerationBusy}
                >
                  {isPromptGenerationBusy ? 'Генерация…' : 'Сгенерировать промты'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetPromptsToDefault}
                  disabled={projectSaving || isPromptGenerationBusy || promptDefaultsLoading}
                >
                  {promptDefaultsLoading ? 'Сбрасываю…' : 'Сбросить к дефолту'}
                </Button>
              </Stack>
              <TextField
                label="Промт извлечения требований"
                value={projectForm.requirementsPrompt}
                onChange={handlePromptFieldChange('requirementsPrompt')}
                disabled={projectSaving || isPromptGenerationBusy}
                multiline
                minRows={6}
                inputProps={{ maxLength: PROJECT_PROMPT_MAX_LENGTH }}
                error={isRequirementsPromptTooLong}
                helperText={`${requirementsPromptLength}/${PROJECT_PROMPT_MAX_LENGTH}. Оставьте пустым, чтобы использовать дефолтный промт системы.`}
              />
              <TextField
                label="Промт анализа конфликтов"
                value={projectForm.conflictsPrompt}
                onChange={handlePromptFieldChange('conflictsPrompt')}
                disabled={projectSaving || isPromptGenerationBusy}
                multiline
                minRows={6}
                inputProps={{ maxLength: PROJECT_PROMPT_MAX_LENGTH }}
                error={isConflictsPromptTooLong}
                helperText={`${conflictsPromptLength}/${PROJECT_PROMPT_MAX_LENGTH}. Оставьте пустым, чтобы использовать дефолтный промт системы.`}
              />
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                  onClick={() => setPromptDrawerOpen(false)}
                  disabled={projectSaving}
                >
                  Закрыть
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveProject}
                  disabled={projectSaving || isPromptGenerationBusy || !projectForm.name.trim() || hasPromptLengthOverflow}
                >
                  {projectSaving ? 'Сохраняю…' : 'Сохранить'}
                </Button>
              </Stack>
            </Stack>
          </Drawer>

          {/* Файлы */}
          <Paper className="surface-glow" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={filesExpanded ? 2 : 0}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                onClick={() => setFilesExpanded((expanded) => !expanded)}
                role="button"
                tabIndex={0}
                aria-expanded={filesExpanded}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setFilesExpanded((expanded) => !expanded);
                  }
                }}
                sx={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <Typography variant="h6">Файлы проекта</Typography>
                <ExpandMoreIcon
                  color="action"
                  sx={{
                    transition: 'transform 180ms ease',
                    transform: filesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Stack>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFilesSelected}
                />
                <Button variant="contained" onClick={openFileDialog} disabled={uploading || llmInteractionLocked}>
                  Добавить файлы
                </Button>
              </div>
            </Stack>

            <Collapse in={filesExpanded} timeout="auto" unmountOnExit>
            {hasFileFlowFeedback && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: (theme) => theme.palette.action.hover,
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Что происходит сейчас</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fileFlowMessage}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {FILE_FLOW_STEPS.map((step, index) => {
                      const isActive = fileFlowStage === step.key && step.key !== 'done';
                      const isDone = fileFlowStageIndex >= 0 && (index < fileFlowStageIndex || fileFlowStage === 'done');
                      return (
                        <Chip
                          key={step.key}
                          size="small"
                          label={`${index + 1}. ${step.label}`}
                          color={isDone ? 'success' : (isActive ? 'primary' : 'default')}
                          variant={isDone || isActive ? 'filled' : 'outlined'}
                        />
                      );
                    })}
                  </Stack>
                  {fileFlowProgress == null ? (
                    <LinearProgress />
                  ) : (
                    <LinearProgress variant="determinate" value={fileFlowProgress} />
                  )}
                </Stack>
              </Paper>
            )}

            {lastCompletedFile && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Typography variant="body2">
                    Готово, файл «{lastCompletedFile.fileName || 'Файл'}» обработан.
                    Найдено требований: {lastCompletedFile.requirementsCount}.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={handleGoToRequirements}>
                      Перейти к требованиям
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleRecheckLastCompletedConflicts}
                      disabled={llmInteractionLocked || processingStages[lastCompletedFile.fileId] === 'conflicts'}
                    >
                      Проверить конфликты
                    </Button>
                  </Stack>
                </Stack>
              </Alert>
            )}

            {fileDownloadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fileDownloadError}
              </Alert>
            )}

            {editedFileDownloadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editedFileDownloadError}
              </Alert>
            )}

            {editedFileDownloadSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditedFileDownloadSuccess(null)}>
                {editedFileDownloadSuccess}
              </Alert>
            )}

            {confluenceSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setConfluenceSuccess(null)}>
                {confluenceSuccess}
              </Alert>
            )}

            {files.length === 0 ? (
              confluenceTargets.length === 0 ? (
                <Typography color="text.secondary">Файлы ещё не загружены.</Typography>
              ) : null
            ) : (
              <Table size="small">
                <TableHead>
                    <TableRow>
                      <TableCell>Файл</TableCell>
                      <TableCell>Тип</TableCell>
                      <TableCell>Режим проверки</TableCell>
                      <TableCell align="right">Размер</TableCell>
                      <TableCell>Загружен</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {files.map(f => {
                    const fileStatus = resolveFileStatus(f);
                    const stageKey = processingStages[f.id];
                    const conflictsInProgress = stageKey === 'conflicts';
                    const manualConflictsDisabled = conflictsInProgress || stageKey === 'requirements';
                    return (
                      <TableRow key={f.id} hover>
                        <TableCell>{f.originalName}</TableCell>
                        <TableCell>{f.contentType || '-'}</TableCell>
                        <TableCell>{formatComparisonMode(f.comparisonMode)}</TableCell>
                        <TableCell align="right">{(f.sizeBytes != null ? f.sizeBytes : 0)}</TableCell>
                        <TableCell>{f.uploadedAt}</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {fileStatus.busy && <CircularProgress size={14} thickness={5} />}
                              <Chip size="small" color={fileStatus.color} label={fileStatus.label} variant="outlined" />
                            </Stack>
                            {fileStatus.hint && (
                              <Typography variant="caption" color="text.secondary">
                                {fileStatus.hint}
                              </Typography>
                            )}
                            {fileStatus.progress != null && (
                              <LinearProgress
                                variant="determinate"
                                value={fileStatus.progress}
                                sx={{ width: 140 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                              <Tooltip title="Скачать исходный файл">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleFileDownload(f)}
                                    disabled={deletingFileId === f.id || Boolean(downloadingFileIds[f.id])}
                                  >
                                    {downloadingFileIds[f.id] ? 'Скачивание…' : 'Скачать'}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Скачать документ с подсветкой найденных конфликтов">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEditedFileDownload(f)}
                                    disabled={deletingFileId === f.id || Boolean(downloadingFileIds[f.id])}
                                  >
                                    {downloadingFileIds[f.id] ? 'Скачивание…' : 'Скачать с правками'}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Запустить проверку конфликтов для этого файла">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleManualConflictsCheck(f.id)}
                                    disabled={llmInteractionLocked || manualConflictsDisabled || deletingFileId === f.id}
                                  >
                                    {conflictsInProgress ? 'Проверка…' : 'Проверить конфликты'}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Удалить файл">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => openDeleteFileDialog(f)}
                                    disabled={deletingFileId === f.id}
                                    aria-label="Удалить файл"
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {confluenceTargets.length > 0 && (
              <Stack spacing={1.25} sx={{ mt: files.length > 0 ? 2 : 0 }}>
                {confluenceTargets.flatMap((target) => [
                  { ...target, isChildPage: false },
                  ...(Array.isArray(target.childPages) ? target.childPages.map((child) => ({
                    ...child, isChildPage: true, parentId: target.id,
                    processingStatus: target.processingStatus,
                    processingProgress: target.processingProgress,
                    processingError: target.processingError,
                  })) : []),
                  ...(Array.isArray(target.attachments) ? target.attachments.map((attachment) => ({
                    ...attachment, isAttachment: true, parentId: target.id,
                  })) : []),
                ]).map((target) => (
                  <Paper key={`${target.isAttachment ? 'attachment' : 'target'}-${target.id}`} variant="outlined" sx={{ p: 1.5, ml: target.isChildPage || target.isAttachment ? 3 : 0 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle2">
                          {target.isAttachment ? `↳ 📎 ${target.title}` : target.isChildPage ? `↳ ${target.title}` : (target.title || `Страница Confluence (${target.pageId || 'без ID'})`)}
                        </Typography>
                        <Typography
                          component="a"
                          href={target.url}
                          target="_blank"
                          rel="noreferrer"
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {target.url}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={target.isAttachment ? 'Confluence · вложенный файл' : target.isChildPage ? 'Confluence · дочерняя страница' : 'Confluence · добавлено'} variant="outlined" color="success" />
                        {(target.isChildPage || (target.processingStatus !== 'DONE' && target.processingStatus !== 'ERROR')) && (
                          <Stack sx={{ width: 140 }} spacing={0.25}>
                            <LinearProgress variant="determinate" value={Number(target.processingProgress) || 0} />
                            <Typography variant="caption" color="text.secondary">
                              {target.processingStatus === 'DONE'
                                ? 'Обработка завершена: 100%'
                                : `Обработка: ${Number(target.processingProgress) || 0}%`}
                            </Typography>
                          </Stack>
                        )}
                        {target.processingStatus === 'ERROR' && <Chip size="small" label="Ошибка обработки" color="error" />}
                        {!target.isChildPage && !target.isAttachment && <IconButton size="small" color="error" aria-label="Удалить страницу Confluence"
                          onClick={() => handleDeleteConfluenceTarget(target)}
                          disabled={deletingConfluenceTargetId === target.id}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
            </Collapse>
          </Paper>

          {/* Требования и LLM отладка */}
          <Paper ref={requirementsSectionRef} className="surface-glow" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">
                  {isAdmin && projectTab === 'llm-debug' ? 'LLM отладка' : 'Требования'}
                </Typography>
              </Stack>

              {isAdmin && (
                <Tabs
                  value={projectTab}
                  onChange={(event, value) => setProjectTab(value)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab value="requirements" label="Требования" />
                  <Tab value="llm-debug" label="LLM отладка" />
                </Tabs>
              )}

              {(!isAdmin || projectTab === 'requirements') && (
                <>
                  {reqErr && <Alert severity="error">{reqErr}</Alert>}
                  {reqLoading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                  ) : reqPage.content.length === 0 ? (
                    <Typography color="text.secondary">Требований пока нет.</Typography>
                  ) : (
                    <Stack spacing={2}>
                      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 960 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ whiteSpace: 'nowrap', width: 40 }}>№</TableCell>
                            <TableCell>Текст требования</TableCell>
                            <TableCell sx={{ width: 200 }}>Метаданные</TableCell>
                            <TableCell>Проблемы</TableCell>
                            <TableCell>Предлагаемая формулировка</TableCell>
                            <TableCell sx={{ width: 180 }}>Качество требования</TableCell>
                            <TableCell sx={{ width: 140 }}>Обратная связь</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRequirements.map((r, i) => {
                              const rid = r.stableId || r.id || `req-${i}`;
                              const pageSize = ((reqPage.size ?? sortedRequirements.length ?? 0) || 1);
                              const displayOrder = ((reqPage.page ?? 0) * pageSize) + i + 1;
                              const sourceLabel = buildSourceLabel(r);
                              const sourceText = getRequirementText(r);
                              const isExpanded = expandedReqTexts.has(rid);
                              const validation = normalizeRequirementValidation(r);
                              const resolvedRewriteSuggestion = resolveAutoRewriteSuggestion(r, validation);
                              const isAutoGenerating = rid ? Boolean(autoRewriteLoadingMap[rid]) : false;
                              const currentFeedback = normalizeRequirementFeedback(r?.userFeedback || r?.user_feedback);
                              const isFeedbackSaving = rid ? Boolean(feedbackSavingMap[rid]) : false;
                              const hasValidationIssues = Array.isArray(validation?.issues) && validation.issues.length > 0;
                              const canAutoRewrite = Boolean(resolvedRewriteSuggestion) || hasValidationIssues;
                              const metaEntries = summarizeRequirementMeta(r);

                              return (
                                <React.Fragment key={rid || i}>
                                  <TableRow
                                    hover
                                    sx={{
                                      opacity: r.enabled === false ? 0.6 : 1,
                                      backgroundColor: r.enabled === false ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                                      transition: 'transform 170ms ease, box-shadow 170ms ease, background-color 170ms ease',
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: 'inset 0 0 0 999px rgba(17, 33, 59, 0.025)',
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, textDecoration: r.enabled === false ? 'line-through' : 'none' }}>
                                      {displayOrder}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', minWidth: 220, textDecoration: r.enabled === false ? 'line-through' : 'none' }}>
                                      <ExpandableTextCell
                                        text={sourceText}
                                        expanded={isExpanded}
                                        onToggle={() => toggleFullText(rid)}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                                      <Stack spacing={0.5}>
                                        {r.enabled === false && (
                                          <Chip size="small" label="Отключено" variant="outlined" color="warning" />
                                        )}
                                        {r.type && <TypeChip type={r.type} />}
                                        {metaEntries.map(({ label, value }) => (
                                          <Typography key={label} variant="body2">
                                            <b>{label}:</b> {value}
                                          </Typography>
                                        ))}
                                        {sourceLabel && !metaEntries.some((m) => m.label === 'Источник') && (
                                          <Typography variant="body2">
                                            <b>Источник:</b> {sourceLabel}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                      {renderIssuesCell(validation?.issues || [])}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                      {renderSuggestedRewriteCell(resolvedRewriteSuggestion, r)}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', width: 140 }}>
                                      <RequirementValidationBlock validation={validation} showFlagsOnly compact={false} />
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top', width: 140 }}>
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title="Все ок">
                                          <span>
                                            <IconButton
                                              size="small"
                                              aria-label="Пометить требование как хорошее"
                                              color={currentFeedback === REQUIREMENT_FEEDBACK.APPROVED ? 'success' : 'default'}
                                              onClick={() => handleRequirementFeedbackChange(r, REQUIREMENT_FEEDBACK.APPROVED)}
                                              disabled={reqLoading || isFeedbackSaving}
                                            >
                                              <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Плохое требование">
                                          <span>
                                            <IconButton
                                              size="small"
                                              aria-label="Пометить требование как плохое"
                                              color={currentFeedback === REQUIREMENT_FEEDBACK.REJECTED ? 'error' : 'default'}
                                              onClick={() => handleRequirementFeedbackChange(r, REQUIREMENT_FEEDBACK.REJECTED)}
                                              disabled={reqLoading || isFeedbackSaving}
                                            >
                                              <CancelIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        {isFeedbackSaving && <CircularProgress size={16} />}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      colSpan={requirementTableColumns}
                                      sx={{
                                        backgroundColor: (theme) => theme.palette.action.hover,
                                        p: 0,
                                      }}
                                    >
                                      <Stack spacing={1.5} sx={{ p: 1.5 }}>
                                        <Stack
                                          direction={{ xs: 'column', sm: 'row' }}
                                          spacing={1}
                                          justifyContent="space-between"
                                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          {(r?.stableId || r?.fileId || r?.chunkIndex != null) && (
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                              {r?.stableId && (
                                                <Typography variant="caption" color="text.secondary">
                                                  ID: {r.stableId}
                                                </Typography>
                                              )}
                                              {r?.fileId && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Файл: {r.fileId}
                                                </Typography>
                                              )}
                                              {typeof r?.chunkIndex === 'number' && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Чанк: {r.chunkIndex}
                                                </Typography>
                                              )}
                                            </Stack>
                                          )}
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                            sx={{ ml: { sm: 'auto' } }}
                                          >
                                            <Tooltip
                                              title={resolvedRewriteSuggestion
                                                ? 'Применить автоматическую доработку требования'
                                                : 'Для этого требования нет замечаний для автодоработки'}
                                            >
                                              <span>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => openAutoRewriteDialog(r, resolvedRewriteSuggestion)}
                                                  disabled={!canAutoRewrite || isAutoGenerating}
                                                >
                                                  {isAutoGenerating ? 'Автодоработка…' : 'Автодоработка'}
                                                </Button>
                                              </span>
                                            </Tooltip>
                                            <Button size="small" variant="outlined" onClick={() => openRequirementEdit(r)}>
                                              Редактировать
                                            </Button>
                                          </Stack>
                                        </Stack>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                        </TableBody>
                      </Table>

                      <Stack direction="row" justifyContent="flex-end" pt={1}>
                        <Pagination
                          page={(reqPage.page ?? 0) + 1}         // MUI — 1-based
                          count={pagesCount}
                          onChange={(e, value) => {
                            const nextPage = value - 1;           // Spring — 0-based
                            if (nextPage !== reqPage.page) {
                              loadReq(nextPage, reqPage.size);
                            }
                          }}
                          disabled={reqLoading}
                        />
                      </Stack>
                    </Stack>
                  )}
                </>
              )}

              {isAdmin && projectTab === 'llm-debug' && (
                <Stack spacing={2}>
                  {llmDebugError && <Alert severity="error">{llmDebugError}</Alert>}
                  {llmDebugLoading && !llmDebug ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <Chip
                        label={`Модель: ${llmDebug?.model || '—'}`}
                        variant="outlined"
                      />
                      <Chip
                        label={`Средняя скорость: ${formatDebugNumber(llmDebug?.averageTokensPerSecond)} ток/с`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`Средняя задержка: ${formatDebugNumber(llmDebug?.averageLatencyMs)} мс`}
                        variant="outlined"
                      />
                      <Chip
                        label={`Запросов: ${Number.isFinite(Number(llmDebug?.requestsCount)) ? Number(llmDebug?.requestsCount) : 0}`}
                        variant="outlined"
                      />
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>

          {/* Конфликты */}
          <Paper className="surface-glow" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Конфликты</Typography>
              <Chip label={`Всего: ${projectConflicts.length}`} />
            </Stack>

            {projectConflictsError && <Alert severity="error">{projectConflictsError}</Alert>}

            {projectConflictsLoading ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            ) : projectConflicts.length === 0 ? (
              <Typography color="text.secondary">Конфликтов пока нет.</Typography>
            ) : (
              <Stack spacing={2}>
                {projectConflicts.map((conflict, idx) => {
                  const leftId = conflict?.left?.stableId || `left-${idx}`;
                  const rightId = conflict?.right?.stableId || `right-${idx}`;
                  const detectedAtDate = conflict?.detectedAt ? new Date(conflict.detectedAt) : null;
                  const detectedLabel = detectedAtDate && !Number.isNaN(detectedAtDate.getTime())
                    ? detectedAtDate.toLocaleString()
                    : null;
                  const key = `${leftId}-${rightId}-${conflict?.detectedAt || idx}`;
                  const extra = conflict?.extra && typeof conflict.extra === 'object' && !Array.isArray(conflict.extra)
                    ? conflict.extra
                    : {};
                  const rationaleText = conflict?.explanation || conflict?.rationale || conflict?.why;
                  const resolutionText = conflict?.resolutionSuggestion || conflict?.resolution;
                  const customExtraBlocks = Object.entries(extra)
                    .filter(([key, value]) => !CONFLICT_RESERVED_FIELDS.has(key) && value != null && value !== '')
                    .map(([key, value]) => (
                      <Typography key={key} variant="body2">
                        <b>{formatFieldTitle(key) || key}:</b> {formatRequirementValue(value)}
                      </Typography>
                    ));
                  const leftRequirement = conflict?.left;
                  const leftSeq = leftRequirement?.displayNumber ?? leftRequirement?.display_number ?? leftRequirement?.seqNumber;
                  const leftTitle = leftSeq != null
                    ? `Требование №${leftSeq}`
                    : leftRequirement?.stableId
                      ? `Требование ${leftRequirement.stableId}`
                      : 'Требование';
                  const rightRequirement = conflict?.right;
                  const rightSeq = rightRequirement?.displayNumber ?? rightRequirement?.display_number ?? rightRequirement?.seqNumber;
                  const rightTitle = rightSeq != null
                    ? `Требование №${rightSeq}`
                    : rightRequirement?.stableId
                      ? `Требование ${rightRequirement.stableId}`
                      : 'Требование';
                  return (
                    <Paper key={conflict?.id || key} variant="outlined" className="surface-glow" sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              color={getRelationColor(conflict?.relation)}
                              label={formatRelationLabel(conflict?.relation, conflict?.score)}
                            />
                            {detectedLabel && (
                              <Typography variant="caption" color="text.secondary">
                                {detectedLabel}
                              </Typography>
                            )}
                          </Stack>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleIgnoreConflictClick(conflict)}
                            disabled={ignoreLoading || isTestGenerationBusy}
                          >
                            Игнорировать
                          </Button>
                        </Stack>
                        {rationaleText && (
                          <Typography variant="body2">{rationaleText}</Typography>
                        )}
                        {resolutionText && (
                          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                            {resolutionText}
                          </Alert>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <RequirementSummary
                            title={leftTitle}
                            requirement={leftRequirement}
                            compact={true}
                          />
                          <RequirementSummary
                            title={rightTitle}
                            requirement={rightRequirement}
                            compact={true}
                          />
                        </Stack>
                        {customExtraBlocks.length > 0 && (
                          <Stack sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">Дополнительные данные:</Typography>
                            {customExtraBlocks}
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
          <ProjectLlmChatWidget project={project} onRequirementsChanged={reloadRequirements} />
        </>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => {
          if (deletingFileId) {
            return;
          }
          closeDeleteFileDialog();
        }}
        disableEscapeKeyDown={Boolean(deletingFileId)}
      >
        <DialogTitle>Удалить файл?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить файл «{deleteDialog.file?.originalName || 'файл'}»?
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteFileDialog} disabled={Boolean(deletingFileId)}>
            Отмена
          </Button>
          <Button
            onClick={handleConfirmDeleteFile}
            color="error"
            disabled={Boolean(deletingFileId)}
          >
            {deletingFileId ? 'Удаление…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadSourceDialogOpen}
        onClose={() => setUploadSourceDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Добавить файлы</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Tooltip title={!confluenceConnected ? 'Для использования страниц из Confluence надо сначала подключить Confluence в настройках системы.' : ''}>
              <span>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LinkIcon />}
                  onClick={openConfluenceDialog}
                  disabled={llmInteractionLocked || !confluenceConnected}
                  sx={{ justifyContent: 'flex-start', width: '100%' }}
                >
                  Из Confluence
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="large"
              startIcon={<CloudUploadIcon />}
              onClick={openComputerFileDialog}
              disabled={llmInteractionLocked}
              sx={{ justifyContent: 'flex-start' }}
            >
              С компьютера
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadSourceDialogOpen(false)}>Отмена</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confluenceDialogOpen}
        onClose={closeConfluenceDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Добавить из Confluence</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Вставьте ссылку на страницу Confluence.
            </DialogContentText>
            <TextField
              label="URL страницы"
              value={confluenceUrl}
              onChange={(event) => setConfluenceUrl(event.target.value)}
              disabled={confluenceLoading}
              autoFocus
              fullWidth
            />
            {confluenceError && <Alert severity="error">{confluenceError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfluenceDialog} disabled={confluenceLoading}>Отмена</Button>
          <Button
            onClick={handleAddConfluenceFile}
            variant="contained"
            disabled={confluenceLoading || !confluenceUrl.trim() || llmInteractionLocked}
          >
            {confluenceLoading ? 'Добавление…' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadModeDialogOpen}
        onClose={closeUploadModeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Режим проверки новых требований</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Выберите, как проверять добавляемые файлы:
            </DialogContentText>
            <FormControl component="fieldset">
              <RadioGroup
                value={uploadComparisonMode}
                onChange={(event) => setUploadComparisonMode(event.target.value)}
                name="upload-comparison-mode"
              >
                <FormControlLabel
                  value="INCLUDE_PREVIOUS"
                  control={<Radio />}
                  label="Проверять с учетом требований из предыдущих файлов"
                />
                <FormControlLabel
                  value="INDEPENDENT"
                  control={<Radio />}
                  label="Проверять независимо от предыдущих файлов"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadModeDialog} disabled={uploading}>Отмена</Button>
          <Button onClick={confirmUploadWithMode} variant="contained" disabled={uploading || llmInteractionLocked || pendingUploadFiles.length === 0}>
            {uploading ? 'Загрузка…' : 'Начать загрузку'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={applyRewriteDialog.open}
        onClose={(_, reason) => {
          const isApplying = applyRewriteDialog.reqId && applyRewriteLoadingMap[applyRewriteDialog.reqId];
          if (isApplying || reason === 'backdropClick') {
            return;
          }
          closeApplyRewriteDialog();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Применить предлагаемую формулировку</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Текущий текст требования будет заменен на предложенный. Действие необратимо.
            </Alert>

            {(applyRewriteDialog.seqNumber != null || applyRewriteDialog.reqId) && (
              <Typography variant="body2" color="text.secondary">
                {applyRewriteDialog.seqNumber != null
                  ? `Требование №${applyRewriteDialog.seqNumber}`
                  : `Требование ${applyRewriteDialog.reqId}`}
              </Typography>
            )}

            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Предлагаемый текст:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {applyRewriteDialog.suggestedText}
              </Typography>
            </Stack>

            {applyRewriteError && (
              <Alert severity="error">{applyRewriteError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApplyRewriteDialog} disabled={Boolean(applyRewriteDialog.reqId && applyRewriteLoadingMap[applyRewriteDialog.reqId])}>
            Отмена
          </Button>
          <Button
            onClick={handleApplySuggestedRewrite}
            variant="contained"
            color="warning"
            disabled={Boolean(applyRewriteDialog.reqId && applyRewriteLoadingMap[applyRewriteDialog.reqId])}
          >
            {applyRewriteDialog.reqId && applyRewriteLoadingMap[applyRewriteDialog.reqId] ? 'Применение…' : 'Применить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialog.open}
        onClose={(_, reason) => {
          if (editSaving) return;
          if (reason === 'backdropClick') {
            closeRequirementEdit();
            return;
          }
          closeRequirementEdit();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Редактирование требования</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {editError && <Alert severity="error">{editError}</Alert>}
            <TextField
              label="Текст требования"
              value={editDialog.text}
              onChange={handleRequirementFieldChange('text')}
              fullWidth
              multiline
              minRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRequirementEdit} disabled={editSaving}>Отмена</Button>
          <Button onClick={handleSaveRequirementEdit} variant="contained" disabled={editSaving || !editDialog.reqId}>
            {editSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ignoreDialog.open}
        onClose={(_, reason) => {
          if (ignoreLoading || isTestGenerationBusy) return;
          if (reason === 'backdropClick') {
            return;
          }
          closeIgnoreDialog();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Подтверждение</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {ignoreDialog.type === 'all'
              ? 'Вы уверены, что хотите игнорировать все конфликты и продолжить?'
              : 'Вы уверены, что хотите игнорировать этот конфликт?'}
          </DialogContentText>
          {projectConflictsError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {projectConflictsError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIgnoreDialog} disabled={ignoreLoading || isTestGenerationBusy}>Отмена</Button>
          <Button
            onClick={() => {
              if (ignoreDialog.type === 'all') {
                handleIgnoreAll();
              } else if (ignoreDialog.conflict) {
                handleIgnoreSingle(ignoreDialog.conflict);
              }
            }}
            variant="contained"
            color="error"
            disabled={ignoreLoading || isTestGenerationBusy || llmInteractionLocked || (ignoreDialog.type === 'single' && !ignoreDialog?.conflict?.id)}
          >
            {ignoreLoading ? 'Игнорирование…' : 'Игнорировать'}
          </Button>
        </DialogActions>
      </Dialog>

      {confOpen && (
        <Dialog open={confOpen} onClose={handleCloseConflicts} fullWidth maxWidth="md">
        <DialogTitle>Конфликты требования</DialogTitle>
        <DialogContent dividers>
          {confLoading ? <CircularProgress /> :
            confItems.length === 0 ? <Typography color="text.secondary">Нет конфликтов.</Typography> :
            <Stack spacing={2}>
              {confItems.map((item, idx) => {
                const c = item || {};
                const extra = c.extra && typeof c.extra === 'object' ? c.extra : {};
                const primaryRef = extra[EXTRA_PRIMARY_REF];
                const relatedRef = extra[EXTRA_RELATED_REF];
                const primarySnapshot = extra[EXTRA_PRIMARY_SNAPSHOT];
                const relatedSnapshot = extra[EXTRA_RELATED_SNAPSHOT];
                const rationaleText = c.explanation || c.rationale || c.why;
                const resolutionText = c.resolutionSuggestion || c.resolution;
                const customExtraBlocks = Object.entries(extra)
                  .filter(([key, value]) => !CONFLICT_RESERVED_FIELDS.has(key) && value != null && value !== '')
                  .map(([key, value]) => (
                    <Typography key={key} variant="body2">
                      <b>{formatFieldTitle(key) || key}:</b> {formatRequirementValue(value)}
                    </Typography>
                  ));

                return (
                  <Paper key={idx} className="surface-glow" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Chip color="error" label={c.relation + (c.score ? ` · ${c.score.toFixed(2)}` : '')} />
                    </Stack>
                    {rationaleText && <Typography variant="body2" sx={{ mt: 1 }}>{rationaleText}</Typography>}
                    {resolutionText && (
                      <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                        {resolutionText}
                      </Alert>
                    )}
                    {primaryRef && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <b>Требование:</b> {primaryRef}
                      </Typography>
                    )}
                    {relatedRef && (
                      <Typography variant="body2">
                        <b>Связанное требование:</b> {relatedRef}
                      </Typography>
                    )}
                    {c.other && (
                      <Stack sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">Противоречит:</Typography>
                        <Typography variant="body2"><b>Action:</b> {c.other.action}</Typography>
                        {c.other.sourceText && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            <b>Текст:</b> {c.other.sourceText}
                          </Typography>
                        )}
                        <Typography variant="body2"><b>Source:</b> {(c.other.sourceFile || '') + (c.other.sourcePage != null ? (', стр. ' + c.other.sourcePage) : '')}</Typography>
                      </Stack>
                    )}
                    {customExtraBlocks.length > 0 && (
                      <Stack sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">Дополнительные данные:</Typography>
                        {customExtraBlocks}
                      </Stack>
                    )}
                    {renderSnapshot('Снимок текущего требования', primarySnapshot)}
                    {renderSnapshot('Снимок конфликтующего требования', relatedSnapshot)}
                  </Paper>
                );
              })}
            </Stack>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConflicts}>Закрыть</Button>
        </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
